import React, { useState, useEffect, useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, Settings, Smartphone, Monitor, Tablet, Tv, HelpCircle, BarChart3, Clock, Info, Ban, ChevronDown, ChevronRight, Layers, Edit3, X, Check, Search } from 'lucide-react';
import axios from 'axios';
import { normaliserObjectifs, objectifActionnable } from '../utils/objectifsCampagne';
import config from '../config';
import AucunCompteGoogleAds from './connectors/AucunCompteGoogleAds';

const API_URL = config.API_URL;

// Icônes par type d'appareil
const DEVICE_ICONS = {
  'MOBILE': Smartphone,
  'DESKTOP': Monitor,
  'TABLET': Tablet,
  'CONNECTED_TV': Tv,
  'OTHER': HelpCircle
};

// Styles des badges de type de campagne
const CHANNEL_BADGE_STYLES = {
  SEARCH: { bg: '#dbeafe', color: '#1e40af', label: 'SEARCH' },
  SHOPPING: { bg: '#fef3c7', color: '#92400e', label: 'SHOPPING' },
  PERFORMANCE_MAX: { bg: '#f3e8ff', color: '#7c3aed', label: 'PMAX' },
  DISPLAY: { bg: '#dcfce7', color: '#166534', label: 'DISPLAY' },
  VIDEO: { bg: '#fee2e2', color: '#991b1b', label: 'VIDEO' },
  DEMAND_GEN: { bg: '#fce7f3', color: '#9d174d', label: 'DEMAND GEN' },
  DISCOVERY: { bg: '#fce7f3', color: '#9d174d', label: 'DISCOVERY' },
  SMART: { bg: '#e0e7ff', color: '#3730a3', label: 'SMART' },
  LOCAL: { bg: '#ccfbf1', color: '#0f766e', label: 'LOCAL' }
};

// Styles des niveaux de confiance (majuscules du backend + minuscules en fallback)
const CONFIDENCE_STYLES = {
  HIGH: { bg: '#dcfce7', color: '#166534', label: 'Haute' },
  MEDIUM: { bg: '#fef3c7', color: '#92400e', label: 'Moyenne' },
  LOW: { bg: '#fee2e2', color: '#991b1b', label: 'Basse' },
  INSUFFICIENT: { bg: '#f3f4f6', color: '#6b7280', label: 'Très basse' },
  // Fallback minuscules
  high: { bg: '#dcfce7', color: '#166534', label: 'Haute' },
  medium: { bg: '#fef3c7', color: '#92400e', label: 'Moyenne' },
  low: { bg: '#fee2e2', color: '#991b1b', label: 'Basse' },
  very_low: { bg: '#f3f4f6', color: '#6b7280', label: 'Très basse' }
};

// Labels français des stratégies d'enchères
const BIDDING_STRATEGY_LABELS = {
  'MANUAL_CPC': 'CPC manuel',
  'MANUAL_CPM': 'CPM manuel',
  'MANUAL_CPV': 'CPV manuel',
  'MAXIMIZE_CLICKS': 'Maximiser les clics',
  'MAXIMIZE_CONVERSIONS': 'Maximiser les conversions',
  'MAXIMIZE_CONVERSION_VALUE': 'Maximiser la valeur de conversion',
  'TARGET_CPA': 'CPA cible',
  'TARGET_ROAS': 'ROAS cible',
  'TARGET_SPEND': 'Dépense cible',
  'TARGET_IMPRESSION_SHARE': 'Taux d\'impressions cible',
  'ENHANCED_CPC': 'CPC optimisé',
  'PERCENT_CPC': 'CPC en pourcentage',
  'COMMISSION': 'Commission',
  'PORTFOLIO': 'Portefeuille'
};

// Paramètres par défaut
const DEFAULT_SETTINGS = {
  level: 'hierarchical',
  campaignType: 'ALL',
  minConversions: 5,
  lambda: 0.03,
  displayPeriod: 14,
  homogeneityThreshold: 0.25,
  overrideCpa: '',
  overrideRoas: '',
  aggressiveness: 'moderate',
  minClicksNoConversion: 100,
  minCostNoConversion: 50
};

// Nom de l'outil pour la sauvegarde des paramètres
const TOOL_NAME = 'deviceBidAdjustments';

const DeviceBidAdjustmentsTool = ({ token, selectedClientId, updateUrlWithAccount }) => {
  // États pour les comptes
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  // États pour les données
  const [recommendations, setRecommendations] = useState([]);
  const [adgroupRecommendations, setAdgroupRecommendations] = useState([]); // Pour vue hiérarchique
  const [smartRecommendations, setSmartRecommendations] = useState([]); // Analyse de variance
  const [varianceStats, setVarianceStats] = useState(null); // Stats globales de variance
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cached, setCached] = useState(false);

  // États pour la vue hiérarchique
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set());
  const [assetGroupsData, setAssetGroupsData] = useState({}); // { campaignId: [assetGroups] } pour PMAX
  const [loadingAssetGroups, setLoadingAssetGroups] = useState(new Set()); // Set de campaignIds en cours de chargement

  // État pour le positionnement du tooltip enrichi
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, visible: false });

  // États pour les ajustements personnalisés par l'utilisateur
  const [customAdjustments, setCustomAdjustments] = useState(new Map());

  // États pour les objectifs personnalisés de campagne
  const [customObjectives, setCustomObjectives] = useState({});
  const [editingObjective, setEditingObjective] = useState(null); // { campaignId, campaignName, currentType, currentValue }
  const [objectiveForm, setObjectiveForm] = useState({ type: 'CPA', cpaValue: '', roasValue: '' });
  const [savingObjective, setSavingObjective] = useState(false);

  // États pour l'application des ajustements
  const [applyingAdjustments, setApplyingAdjustments] = useState(new Set()); // Set de keys "entityId-device"
  const [appliedAdjustments, setAppliedAdjustments] = useState(new Set()); // Ajustements déjà appliqués
  const [applyError, setApplyError] = useState(null);

  // Valeurs calculées pour le modal d'objectif (évite les recalculs dans le JSX)
  const objectiveModalValues = useMemo(() => {
    const currentValue = objectiveForm.type === 'CPA' ? objectiveForm.cpaValue : objectiveForm.roasValue;
    return {
      currentValue,
      hasValue: !!currentValue,
      isDisabled: !currentValue || savingObjective
    };
  }, [objectiveForm.type, objectiveForm.cpaValue, objectiveForm.roasValue, savingObjective]);

  // États pour les filtres et paramètres
  const [showFilters, setShowFilters] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [savedSettings, setSavedSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // États pour les filtres inline
  const [searchFilter, setSearchFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [strategyFilter, setStrategyFilter] = useState('ALL');

  // Charger les paramètres depuis l'API quand le compte change
  useEffect(() => {
    const loadSettings = async () => {
      if (!token || !selectedAccount) {
        setSettings(DEFAULT_SETTINGS);
        setSettingsLoaded(false);
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/api/tools/settings/${TOOL_NAME}/${selectedAccount}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.data.success && response.data.settings) {
          const newSettings = { ...DEFAULT_SETTINGS, ...response.data.settings };
          setSettings(newSettings);
          setSavedSettings(newSettings);
        } else {
          setSettings(DEFAULT_SETTINGS);
          setSavedSettings(DEFAULT_SETTINGS);
        }
      } catch (e) {
        console.error('Erreur chargement paramètres:', e);
        setSettings(DEFAULT_SETTINGS);
      } finally {
        setSettingsLoaded(true);
      }
    };
    loadSettings();
  }, [token, selectedAccount]);

  // Sauvegarder les paramètres dans l'API quand ils changent (après chargement initial)
  useEffect(() => {
    if (!settingsLoaded || !token || !selectedAccount) return;

    const saveSettings = async () => {
      try {
        await axios.post(`${API_URL}/api/tools/settings/${TOOL_NAME}/${selectedAccount}`,
          { settings },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } catch (e) {
        console.error('Erreur sauvegarde paramètres:', e);
      }
    };

    // Debounce pour éviter trop d'appels API
    const timeoutId = setTimeout(saveSettings, 500);
    return () => clearTimeout(timeoutId);
  }, [settings, settingsLoaded, token, selectedAccount]);

  // Vérifier si les paramètres sont personnalisés (différents des valeurs par défaut)
  const hasCustomSettings = useMemo(() => {
    return Object.keys(DEFAULT_SETTINGS).some(key => {
      const defaultVal = DEFAULT_SETTINGS[key];
      const currentVal = settings[key];
      // Comparer en tenant compte des types (string vs number)
      return String(defaultVal) !== String(currentVal);
    });
  }, [settings]);

  // Vérifier si les paramètres ont été modifiés (non sauvegardés)
  const hasUnsavedChanges = useMemo(() => {
    return Object.keys(DEFAULT_SETTINGS).some(key => {
      return String(settings[key]) !== String(savedSettings[key]);
    });
  }, [settings, savedSettings]);

  // Extraire les types de campagne et stratégies disponibles dans les données
  const availableCampaignTypes = useMemo(() => {
    const types = new Set();
    recommendations.forEach(rec => {
      if (rec.campaignType) types.add(rec.campaignType);
    });
    return Array.from(types).map(type => ({
      value: type,
      label: CHANNEL_BADGE_STYLES[type]?.label || String(type)
    })).sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [recommendations]);

  const availableBiddingStrategies = useMemo(() => {
    const strategies = new Set();
    recommendations.forEach(rec => {
      if (rec.biddingStrategyType) strategies.add(rec.biddingStrategyType);
    });
    return Array.from(strategies).map(strategy => ({
      value: strategy,
      label: BIDDING_STRATEGY_LABELS[strategy] || String(strategy)
    })).sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [recommendations]);

  // Filtres d'affichage
  const [recommendationFilter, setRecommendationFilter] = useState(null);
  const [deviceFilter, setDeviceFilter] = useState(null);

  // État pour le tri des colonnes
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });

  // Fonction pour gérer le tri
  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Icône de tri
  const SortIcon = ({ column }) => {
    if (sortConfig.column !== column) {
      return null; // Pas d'icône si la colonne n'est pas triée
    }
    return <span style={{ marginLeft: '4px' }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;
  };

  // Charger les comptes au montage et quand le client change
  useEffect(() => {
    if (token) {
      // Réinitialiser tous les états quand le client change
      setSelectedAccount('');
      setRecommendations([]);
      setAdgroupRecommendations([]);
      setSmartRecommendations([]);
      setVarianceStats(null);
      setSummary(null);
      setError(null);
      setSettingsLoaded(false);
      // Recharger les comptes
      loadAccounts();
    }
  }, [token, selectedClientId]);

  // Charger les données quand le compte est sélectionné
  useEffect(() => {
    if (token && selectedAccount) {
      fetchData();
    }
  }, [token, selectedAccount]);

  // Fonction pour charger la liste des comptes
  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const response = await axios.get(`${API_URL}/api/library?usage=outils`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success && response.data.library) {
        const data = response.data;
        const googleSheetsItems = data.library?.GOOGLE_SHEETS || [];
        const googleAdsItems = data.library?.GOOGLE_ADS || [];

        const adsItems = [
          ...googleSheetsItems.filter(item => item.type === 'GOOGLE_ADS'),
          ...googleAdsItems.filter(item => item.type === 'GOOGLE_ADS')
        ];

        let filteredAccounts = adsItems;
        if (selectedClientId) {
          filteredAccounts = await filterAccountsByClient(adsItems, selectedClientId);
        }

        // Trier les comptes par ordre alphabétique
        const sortedAccounts = [...filteredAccounts].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
        );
        setAccounts(sortedAccounts);

        if (sortedAccounts.length === 1) {
          const firstAccount = sortedAccounts[0];
          const accountId = firstAccount.sourceId || firstAccount.id;
          setSelectedAccount(accountId);
        }
      } else {
        setError('Format de réponse inattendu pour les comptes');
      }
    } catch (error) {
      console.error('Erreur chargement comptes:', error);
      setError('Impossible de charger les comptes Google Ads: ' + error.message);
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Filtrer les comptes selon le client sélectionné
  const filterAccountsByClient = async (accounts, clientId) => {
    if (!clientId) return accounts;

    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success && data.sites) {
        const clientSites = data.sites.filter(site => site.clientId === clientId);
        const authorizedAccountIds = clientSites.flatMap(site => site.accountIds || []);

        return accounts.filter(account => {
          const matchesId = authorizedAccountIds.includes(account.id);
          const matchesSourceId = authorizedAccountIds.includes(account.sourceId);
          return matchesId || matchesSourceId;
        });
      }
    } catch (err) {
      console.error('Erreur filtrage comptes:', err);
    }

    return accounts;
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Mode hiérarchique : utiliser le endpoint /smart avec analyse de variance
      if (settings.level === 'hierarchical') {
        const params = {
          clientId: selectedAccount,
          campaignType: settings.campaignType,
          minConversions: settings.minConversions,
          lambda: settings.lambda,
          displayPeriod: settings.displayPeriod,
          homogeneityThreshold: settings.homogeneityThreshold,
          aggressiveness: settings.aggressiveness,
          minClicksNoConversion: settings.minClicksNoConversion,
          minCostNoConversion: settings.minCostNoConversion,
          useCache: 'false'
        };

        const response = await axios.get(`${API_URL}/api/tools/device-bid-adjustments/smart`, {
          params,
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.data.success) {
          // Stocker les recommandations intelligentes avec analyse de variance
          setSmartRecommendations(response.data.smartRecommendations || []);
          setVarianceStats(response.data.stats || null);

          // Stocker aussi les recommandations brutes pour l'affichage
          setRecommendations(response.data.rawCampaignRecommendations?.recommendations || []);
          setAdgroupRecommendations(response.data.rawAdgroupRecommendations?.recommendations || []);
          setSummary(response.data.rawCampaignRecommendations?.summary || null);
          setCached(response.data.cached || false);

          // Charger les objectifs personnalisés depuis la réponse
          if (response.data.customObjectives) {
            setCustomObjectives(response.data.customObjectives);
          }

          // Auto-déplier les campagnes avec outliers
          const campaignsWithOutliers = new Set();
          (response.data.smartRecommendations || []).forEach(sr => {
            if (sr.level === 'ADGROUP_OUTLIERS' && sr.adgroupOutliers?.length > 0) {
              campaignsWithOutliers.add(sr.campaign?.entityId);
            }
          });
          if (campaignsWithOutliers.size > 0) {
            setExpandedCampaigns(campaignsWithOutliers);
          }
        } else {
          setError(response.data.error || 'Erreur lors de la récupération des données');
        }
      } else {
        // Mode simple (campaign ou adgroup seul)
        const params = {
          clientId: selectedAccount,
          level: settings.level,
          campaignType: settings.campaignType,
          minConversions: settings.minConversions,
          lambda: settings.lambda,
          displayPeriod: settings.displayPeriod,
          aggressiveness: settings.aggressiveness,
          minClicksNoConversion: settings.minClicksNoConversion,
          minCostNoConversion: settings.minCostNoConversion,
          useCache: 'false'
        };

        // Ajouter les overrides s'ils sont définis
        if (settings.overrideCpa && parseFloat(settings.overrideCpa) > 0) {
          params.overrideCpa = parseFloat(settings.overrideCpa);
        }
        if (settings.overrideRoas && parseFloat(settings.overrideRoas) > 0) {
          params.overrideRoas = parseFloat(settings.overrideRoas) / 100;
        }

        const response = await axios.get(`${API_URL}/api/tools/device-bid-adjustments`, {
          params,
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.data.success) {
          setRecommendations(response.data.recommendations || []);
          setAdgroupRecommendations([]);
          setSummary(response.data.summary || null);
          setCached(response.data.cached || false);
          // Charger les objectifs personnalisés depuis la réponse
          if (response.data.customObjectives) {
            setCustomObjectives(response.data.customObjectives);
          }
        } else {
          setError(response.data.error || 'Erreur lors de la récupération des données');
        }
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Charger les objectifs personnalisés
  const fetchCustomObjectives = async () => {
    if (!token || !selectedAccount) return;
    try {
      const response = await axios.get(`${API_URL}/api/tools/campaign-objectives`, {
        params: { accountId: selectedAccount },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        /**
         * L'API rend désormais **une liste** d'objectifs par campagne. Cet outil n'en retient qu'un,
         * et seulement parmi les deux qu'il sait interpréter : `objectifActionnable` filtre le CPC
         * et le volume de conversions, et donne la priorité au CPA sur le ROAS — même arbitrage que
         * le serveur. `normaliserObjectifs` accepte au passage l'ancienne forme.
         */
        const parCampagne = normaliserObjectifs(response.data.objectives || {});
        const retenus = {};
        Object.entries(parCampagne).forEach(([campaignId, objectifs]) => {
          const retenu = objectifActionnable(objectifs);
          if (retenu) retenus[campaignId] = retenu;
        });
        setCustomObjectives(retenus);
      }
    } catch (err) {
      console.error('Erreur chargement objectifs personnalisés:', err);
    }
  };

  // Sauvegarder un objectif personnalisé
  const saveCustomObjective = async () => {
    const currentValue = objectiveForm.type === 'CPA' ? objectiveForm.cpaValue : objectiveForm.roasValue;
    if (!editingObjective || !currentValue) return;

    setSavingObjective(true);
    try {
      const response = await axios.post(`${API_URL}/api/tools/campaign-objectives`, {
        accountId: selectedAccount,
        campaignId: editingObjective.campaignId,
        objectiveType: objectiveForm.type,
        objectiveValue: parseFloat(currentValue)
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        /**
         * Relire au lieu de deviner.
         *
         * Une campagne peut porter plusieurs objectifs : enregistrer un ROAS n'efface plus le CPA
         * qui existait. Or c'est le CPA qui reste retenu par l'outil. Une mise à jour optimiste
         * afficherait donc le ROAS qu'on vient de saisir, puis basculerait sur le CPA au
         * rechargement suivant — mieux vaut afficher tout de suite ce qui sera effectivement
         * appliqué.
         */
        await fetchCustomObjectives();
        setEditingObjective(null);
        setObjectiveForm({ type: 'CPA', cpaValue: '', roasValue: '' });
        // Recharger les données pour recalculer les recommandations avec le nouvel objectif
        fetchData();
      }
    } catch (err) {
      console.error('Erreur sauvegarde objectif:', err);
      alert('Erreur lors de la sauvegarde de l\'objectif');
    } finally {
      setSavingObjective(false);
    }
  };

  // Supprimer un objectif personnalisé
  const deleteCustomObjective = async (campaignId) => {
    /**
     * Le type fait partie du chemin : sans lui, la route désactivait **tous** les objectifs de la
     * campagne, y compris ceux que cet outil n'affiche pas.
     */
    const objectifAffiche = customObjectives[campaignId];
    if (!objectifAffiche) return;

    if (!window.confirm(`Supprimer l'objectif ${objectifAffiche.objectiveType} personnalisé ? Les recommandations utiliseront à nouveau l'objectif Google Ads.`)) return;

    try {
      const response = await axios.delete(`${API_URL}/api/tools/campaign-objectives/${campaignId}/${objectifAffiche.objectiveType}`, {
        params: { accountId: selectedAccount },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setCustomObjectives(prev => {
          const newObj = { ...prev };
          delete newObj[campaignId];
          return newObj;
        });
        // Recharger les données pour recalculer les recommandations
        fetchData();
      }
    } catch (err) {
      console.error('Erreur suppression objectif:', err);
    }
  };

  // Ouvrir le modal d'édition d'objectif
  const openObjectiveEditor = (campaignId, campaignName, targetCpa, targetRoas) => {
    const customObj = customObjectives[campaignId];

    // Initialiser les deux valeurs (CPA et ROAS) séparément
    let cpaValue = targetCpa ? targetCpa.toFixed(2) : '';
    let roasValue = targetRoas ? (targetRoas * 100).toFixed(0) : '';
    let activeType = 'CPA';

    if (customObj) {
      // Si un objectif personnalisé existe, l'utiliser
      if (customObj.objectiveType === 'CPA') {
        cpaValue = customObj.objectiveValue.toString();
        activeType = 'CPA';
      } else {
        roasValue = customObj.objectiveValue.toString();
        activeType = 'ROAS';
      }
    } else if (targetRoas && !targetCpa) {
      // Si seulement ROAS est défini, sélectionner ROAS
      activeType = 'ROAS';
    }

    setObjectiveForm({ type: activeType, cpaValue, roasValue });
    setEditingObjective({ campaignId, campaignName, targetCpa, targetRoas });
  };

  // Note: Les objectifs personnalisés sont maintenant chargés via fetchData
  // fetchCustomObjectives est gardé pour rafraîchir après une sauvegarde

  // Ordre d'affichage des appareils
  const DEVICE_ORDER = ['DESKTOP', 'MOBILE', 'TABLET', 'CONNECTED_TV', 'OTHER'];
  const DEVICE_NAMES = {
    'DESKTOP': 'Ordinateur',
    'MOBILE': 'Mobile',
    'TABLET': 'Tablette',
    'CONNECTED_TV': 'TV connectée',
    'OTHER': 'Autre'
  };

  // Charger les asset groups d'une campagne PMAX
  const fetchAssetGroups = async (campaignId, campaignName) => {
    if (assetGroupsData[campaignId] || loadingAssetGroups.has(campaignId)) return;

    setLoadingAssetGroups(prev => new Set([...prev, campaignId]));

    try {
      const response = await axios.post(`${API_URL}/api/dashboard/asset-groups`, {
        sources: [selectedAccount],
        period: `LAST_${settings.displayPeriod}_DAYS`,
        campaignFilter: { id: campaignId, name: campaignName }
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success && response.data.data?.assetGroups) {
        setAssetGroupsData(prev => ({
          ...prev,
          [campaignId]: response.data.data.assetGroups
        }));
      } else {
        setAssetGroupsData(prev => ({
          ...prev,
          [campaignId]: []
        }));
      }
    } catch (err) {
      console.error('❌ Erreur chargement asset groups:', err);
      setAssetGroupsData(prev => ({
        ...prev,
        [campaignId]: []
      }));
    } finally {
      setLoadingAssetGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(campaignId);
        return newSet;
      });
    }
  };

  // Toggle expansion d'une campagne (vue hiérarchique)
  const toggleCampaignExpand = (campaignId, campaignType, campaignName) => {
    const isCurrentlyExpanded = expandedCampaigns.has(campaignId);

    setExpandedCampaigns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(campaignId)) {
        newSet.delete(campaignId);
      } else {
        newSet.add(campaignId);
      }
      return newSet;
    });

    // Si on ouvre une campagne PMAX, charger les asset groups
    if (!isCurrentlyExpanded) {
      const isPmax = campaignType === 'PERFORMANCE_MAX' || campaignType === 10 || campaignType === '10';
      if (isPmax && !assetGroupsData[campaignId]) {
        fetchAssetGroups(campaignId, campaignName);
      }
    }
  };

  // Expand/collapse all campaigns
  const expandAllCampaigns = () => {
    const allCampaignIds = [...new Set(recommendations.map(r => r.campaignId || r.entityId))];
    setExpandedCampaigns(new Set(allCampaignIds));
  };

  const collapseAllCampaigns = () => {
    setExpandedCampaigns(new Set());
  };

  // Gestion des ajustements personnalisés
  const getCustomAdjustment = (entityId, device) => {
    const key = `${entityId}-${device}`;
    return customAdjustments.get(key);
  };

  const setCustomAdjustment = (entityId, device, value, validate = false) => {
    const key = `${entityId}-${device}`;
    setCustomAdjustments(prev => {
      const newMap = new Map(prev);
      if (value === null || value === undefined) {
        newMap.delete(key);
      } else if (value === '' || value === '-') {
        // Permettre temporairement les valeurs vides pendant l'édition
        newMap.set(key, value);
      } else {
        let numVal = parseInt(value, 10) || 0;
        if (validate) {
          numVal = Math.max(-100, Math.min(100, numVal));
        }
        newMap.set(key, numVal);
      }
      return newMap;
    });
  };

  const resetCustomAdjustment = (entityId, device) => {
    setCustomAdjustment(entityId, device, null);
  };

  const resetAllCustomAdjustments = () => {
    setCustomAdjustments(new Map());
  };

  // Appliquer un ajustement via l'API Google Ads
  const applyAdjustment = async (entityId, entityType, device, adjustmentPercent, entityName) => {
    const key = `${entityId}-${device}`;

    // Vérifier si déjà en cours
    if (applyingAdjustments.has(key)) {
      return;
    }

    setApplyingAdjustments(prev => new Set([...prev, key]));
    setApplyError(null);

    try {
      console.log(`📱 [APPLY] Envoi: ${entityType} ${entityId}, ${device}: ${adjustmentPercent}%`);

      const response = await axios.post(
        `${API_URL}/api/tools/device-bid-adjustments/apply`,
        {
          clientId: selectedAccount,
          entityId,
          entityType,
          device,
          adjustmentPercent: Math.round(adjustmentPercent)
        },
        {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 30000
        }
      );

      if (response.data.success) {
        console.log(`✅ [APPLY] Succès:`, response.data.message);
        setAppliedAdjustments(prev => new Set([...prev, key]));

        // Afficher une notification de succès (optionnel)
        alert(`Ajustement appliqué avec succès !\n\n${entityName}\n${device}: ${adjustmentPercent >= 0 ? '+' : ''}${Math.round(adjustmentPercent)}%`);

        // Rafraîchir les données après 2 secondes
        setTimeout(() => {
          fetchData();
        }, 2000);
      } else {
        throw new Error(response.data.error || 'Erreur inconnue');
      }

    } catch (error) {
      console.error(`❌ [APPLY] Erreur:`, error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de l\'application';
      setApplyError(`${entityName} - ${device}: ${errorMessage}`);
      alert(`Erreur lors de l'application de l'ajustement:\n\n${errorMessage}`);
    } finally {
      setApplyingAdjustments(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  // Indexer les smartRecommendations par campaignId-device pour accès rapide
  const smartRecoIndex = useMemo(() => {
    const index = new Map();
    smartRecommendations.forEach(sr => {
      if (sr.campaign) {
        const key = `${sr.campaign.entityId}-${sr.campaign.device}`;
        index.set(key, sr);
      }
    });
    return index;
  }, [smartRecommendations]);

  // Structure hiérarchique pour la vue hiérarchique avec données de variance
  const hierarchicalData = useMemo(() => {
    if (settings.level !== 'hierarchical') return [];

    // Grouper les campagnes
    const campaignsMap = new Map();
    recommendations.forEach(rec => {
      const campaignId = rec.entityId;
      if (!campaignsMap.has(campaignId)) {
        campaignsMap.set(campaignId, {
          campaignId,
          campaignName: rec.entityName,
          campaignType: rec.campaignType,
          biddingStrategyType: rec.biddingStrategyType,
          targetCpa: rec.referenceMetric === 'CPA' ? rec.referenceValue : null,
          targetRoas: rec.referenceMetric === 'ROAS' ? rec.referenceValue : null,
          devices: {},
          // Données de variance par device
          varianceByDevice: {}
        });
      }
      campaignsMap.get(campaignId).devices[rec.device] = rec;

      // Récupérer les données de variance pour ce device
      const smartKey = `${campaignId}-${rec.device}`;
      const smartReco = smartRecoIndex.get(smartKey);
      if (smartReco) {
        campaignsMap.get(campaignId).varianceByDevice[rec.device] = {
          level: smartReco.level,
          recommendationLevel: smartReco.recommendationLevel,
          reason: smartReco.reason,
          stats: smartReco.stats,
          adgroupOutliers: smartReco.adgroupOutliers || [],
          skipRecommendation: smartReco.campaign?.skipRecommendation
        };
      }
    });

    // Grouper les adgroups par campagne et device
    const adgroupsMap = new Map();
    adgroupRecommendations.forEach(rec => {
      const key = rec.campaignId;
      if (!adgroupsMap.has(key)) {
        adgroupsMap.set(key, new Map());
      }
      const adgroupId = rec.entityId;
      if (!adgroupsMap.get(key).has(adgroupId)) {
        adgroupsMap.get(key).set(adgroupId, {
          adgroupId,
          adgroupName: rec.entityName,
          campaignId: rec.campaignId,
          campaignName: rec.campaignName,
          biddingStrategyType: rec.biddingStrategyType,
          devices: {},
          // Statut outlier par device
          outlierStatusByDevice: {}
        });
      }
      adgroupsMap.get(key).get(adgroupId).devices[rec.device] = rec;
    });

    // Marquer les outliers dans les adgroups
    smartRecommendations.forEach(sr => {
      if (sr.adgroupOutliers && sr.adgroupOutliers.length > 0) {
        const campaignAdgroups = adgroupsMap.get(sr.campaign?.campaignId || sr.campaign?.entityId);
        if (campaignAdgroups) {
          sr.adgroupOutliers.forEach(outlier => {
            const adgroup = campaignAdgroups.get(outlier.entityId);
            if (adgroup) {
              adgroup.outlierStatusByDevice[outlier.device] = {
                status: outlier.outlierStatus,
                deviationPercent: outlier.deviationPercent,
                reason: outlier.reason
              };
            }
          });
        }
      }
    });

    // Construire la structure finale
    const result = [];
    campaignsMap.forEach((campaign, campaignId) => {
      const adgroups = adgroupsMap.get(campaignId);

      // Calculer les stats agrégées de variance pour la campagne
      const varianceDevices = Object.entries(campaign.varianceByDevice);
      const hasOutliers = varianceDevices.some(([, v]) => v.level === 'ADGROUP_OUTLIERS');
      const avgCV = varianceDevices.length > 0
        ? varianceDevices.reduce((sum, [, v]) => sum + (v.stats?.cv || 0), 0) / varianceDevices.length
        : null;

      result.push({
        type: 'campaign',
        ...campaign,
        adgroupCount: adgroups ? adgroups.size : 0,
        adgroups: adgroups ? Array.from(adgroups.values()) : [],
        // Résumé variance
        hasOutliers,
        avgCV,
        outlierCount: varianceDevices.reduce((sum, [, v]) => sum + (v.adgroupOutliers?.length || 0), 0)
      });
    });

    return result;
  }, [recommendations, adgroupRecommendations, smartRecoIndex, settings.level]);

  // Filtrage et tri des données hiérarchiques
  const sortedHierarchicalData = useMemo(() => {
    let result = [...hierarchicalData];

    // Appliquer les filtres
    // Filtre par recherche (nom de campagne)
    if (searchFilter.trim()) {
      const search = searchFilter.toLowerCase().trim();
      result = result.filter(campaign =>
        campaign.campaignName?.toLowerCase().includes(search)
      );
    }

    // Filtre par type de campagne
    if (typeFilter !== 'ALL') {
      result = result.filter(campaign => campaign.campaignType === typeFilter);
    }

    // Filtre par stratégie d'enchères
    if (strategyFilter !== 'ALL') {
      result = result.filter(campaign => campaign.biddingStrategyType === strategyFilter);
    }

    // Si pas de tri configuré, retourner les résultats filtrés
    if (!sortConfig.column || !result.length) return result;

    // Fonction pour calculer les totaux d'une campagne
    const getCampaignTotals = (campaign) => {
      let clicks = 0, conversions = 0, cost = 0, conversionsValue = 0;
      Object.values(campaign.devices).forEach(rec => {
        clicks += rec.displayClicks || 0;
        conversions += rec.displayConversions || 0;
        cost += rec.displayCost || 0;
        conversionsValue += rec.displayConversionsValue || 0;
      });
      const cpa = conversions > 0 ? cost / conversions : 0;
      const roas = cost > 0 ? conversionsValue / cost : 0;
      const convRate = clicks > 0 ? (conversions / clicks) * 100 : 0;
      const cpc = clicks > 0 ? cost / clicks : 0;
      return { clicks, conversions, cost, cpa, roas, convRate, cpc };
    };

    return result.sort((a, b) => {
      const totalsA = getCampaignTotals(a);
      const totalsB = getCampaignTotals(b);

      let valueA, valueB;
      switch (sortConfig.column) {
        case 'name':
          valueA = a.campaignName.toLowerCase();
          valueB = b.campaignName.toLowerCase();
          break;
        case 'clicks':
          valueA = totalsA.clicks;
          valueB = totalsB.clicks;
          break;
        case 'convRate':
          valueA = totalsA.convRate;
          valueB = totalsB.convRate;
          break;
        case 'conversions':
          valueA = totalsA.conversions;
          valueB = totalsB.conversions;
          break;
        case 'cpc':
          valueA = totalsA.cpc;
          valueB = totalsB.cpc;
          break;
        case 'cost':
          valueA = totalsA.cost;
          valueB = totalsB.cost;
          break;
        case 'cpa':
          valueA = totalsA.cpa;
          valueB = totalsB.cpa;
          break;
        case 'roas':
          valueA = totalsA.roas;
          valueB = totalsB.roas;
          break;
        default:
          return 0;
      }

      if (typeof valueA === 'string') {
        return sortConfig.direction === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
      return sortConfig.direction === 'asc' ? valueA - valueB : valueB - valueA;
    });
  }, [hierarchicalData, sortConfig, searchFilter, typeFilter, strategyFilter]);

  // Transformer les recommandations (tableau plat par device) en lignes pour le tableau
  const flattenedData = useMemo(() => {
    // Le backend renvoie un tableau plat où chaque item est une recommandation pour un device
    // On doit d'abord grouper par entityId, puis créer les lignes du tableau

    // Grouper les recommandations par entityId
    const entitiesMap = new Map();

    recommendations.forEach(rec => {
      const entityId = rec.entityId;

      if (!entitiesMap.has(entityId)) {
        entitiesMap.set(entityId, {
          entityId: rec.entityId,
          entityName: rec.entityName,
          entityType: rec.entityType,
          campaignId: rec.campaignId,
          campaignName: rec.campaignName,
          campaignType: rec.campaignType,
          adGroupId: rec.adGroupId,
          adGroupName: rec.adGroupName,
          biddingStrategyType: rec.biddingStrategyType,
          targetCpa: rec.referenceMetric === 'CPA' ? rec.referenceValue : null,
          targetRoas: rec.referenceMetric === 'ROAS' ? rec.referenceValue : null,
          devices: {}
        });
      }

      // Ajouter ce device à l'entité
      entitiesMap.get(entityId).devices[rec.device] = rec;
    });

    const rows = [];

    // Transformer en lignes pour le tableau
    entitiesMap.forEach(entity => {
      // Afficher uniquement les appareils avec des données
      const entityDeviceKeys = Object.keys(entity.devices);
      // Trier selon l'ordre défini
      const sortedDevices = entityDeviceKeys.sort((a, b) => {
        const orderA = DEVICE_ORDER.indexOf(a);
        const orderB = DEVICE_ORDER.indexOf(b);
        return (orderA === -1 ? 999 : orderA) - (orderB === -1 ? 999 : orderB);
      });

      // Calculer les totaux pour l'entité (sur TOUS les appareils avec données)
      // Utiliser les métriques d'affichage pour des totaux cohérents
      let totalCost = 0;
      let totalConversions = 0;
      let totalConversionsValue = 0;
      let totalClicks = 0;
      let totalImpressions = 0;

      entityDeviceKeys.forEach(deviceKey => {
        const deviceData = entity.devices[deviceKey];
        if (deviceData) {
          totalCost += deviceData.displayCost || 0;
          totalConversions += deviceData.displayConversions || 0;
          totalConversionsValue += deviceData.displayConversionsValue || 0;
          totalClicks += deviceData.displayClicks || 0;
          totalImpressions += deviceData.displayImpressions || 0;
        }
      });

      // Calculer CPA moyen et ROAS moyen de l'entité
      const entityAvgCpa = totalConversions > 0 ? totalCost / totalConversions : 0;
      const entityAvgRoas = totalCost > 0 ? totalConversionsValue / totalCost : 0;

      // Traiter chaque appareil
      sortedDevices.forEach(deviceKey => {
        const deviceData = entity.devices[deviceKey];

        if (deviceData) {
          // Utiliser les métriques d'affichage (période standard, non pondérées)
          const cost = deviceData.displayCost || 0;
          const conversions = deviceData.displayConversions || 0;
          const conversionsValue = deviceData.displayConversionsValue || 0;
          const clicks = deviceData.displayClicks || 0;
          const impressions = deviceData.displayImpressions || 0;
          const displayPeriod = deviceData.displayPeriod || 14;

          // Déterminer le type de recommandation basé sur status et finalAdjustmentPercent
          let recommendationType = 'maintain';
          if (deviceData.status === 'INSUFFICIENT_DATA') {
            recommendationType = 'insufficient_data';
          } else if (deviceData.status === 'NOT_SUPPORTED') {
            recommendationType = 'not_applicable';
          } else if (deviceData.status === 'INFORMATIONAL') {
            recommendationType = 'not_applicable';
          } else if (deviceData.finalAdjustmentPercent !== null) {
            if (deviceData.finalAdjustmentPercent === -100) {
              recommendationType = 'exclusion';
            } else if (deviceData.finalAdjustmentPercent > 5) {
              recommendationType = 'increase';
            } else if (deviceData.finalAdjustmentPercent < -5) {
              recommendationType = 'decrease';
            }
          }

          rows.push({
            // Identification
            entityId: entity.entityId,
            entityName: entity.entityName,
            entityType: entity.entityType,
            campaignId: entity.campaignId,
            campaignName: entity.campaignName,
            campaignType: entity.campaignType,
            adGroupId: entity.adGroupId,
            adGroupName: entity.adGroupName,
            biddingStrategyType: entity.biddingStrategyType,
            targetCpa: entity.targetCpa,
            targetRoas: entity.targetRoas,
            entityAvgCpa: entityAvgCpa,
            entityAvgRoas: entityAvgRoas,
            // Appareil
            device: deviceKey,
            deviceName: DEVICE_NAMES[deviceKey] || deviceKey,
            // Métriques d'affichage (période standard)
            cost: cost,
            conversions: conversions,
            conversionsValue: conversionsValue,
            clicks: clicks,
            impressions: impressions,
            cpa: deviceData.displayCPA || 0,
            roas: deviceData.displayROAS || 0,
            ctr: deviceData.displayCTR || 0,
            cpc: deviceData.displayCPC || 0,
            displayPeriod: displayPeriod,
            // Métriques pondérées (pour l'analyse)
            weightedConversions: deviceData.weightedConversions || 0,
            // Analyse
            lookbackWindow: deviceData.lookbackWindow,
            confidenceScore: deviceData.confidenceScore || 0,
            confidenceLevel: deviceData.confidenceLevel || 'INSUFFICIENT',
            // Recommandation
            recommendationType: recommendationType,
            adjustmentPercent: deviceData.rawAdjustmentPercent,
            reason: deviceData.reason || deviceData.constraintWarning,
            // Contraintes
            constraintType: deviceData.adjustmentType,
            constraintLabel: deviceData.adjustmentLabel,
            canApply: deviceData.canApply !== false,
            constraintWarning: deviceData.constraintWarning,
            finalAdjustmentPercent: deviceData.finalAdjustmentPercent,
            // Ajustement actuel
            currentAdjustment: deviceData.currentAdjustment || 0,
            isInheritedAdjustment: deviceData.isInheritedAdjustment || false,
            // Flag
            isTotal: false
          });
        }
      });

      // Ajouter la ligne Total pour cette entité
      rows.push({
        entityId: entity.entityId,
        entityName: entity.entityName,
        entityType: entity.entityType,
        campaignId: entity.campaignId,
        campaignName: entity.campaignName,
        campaignType: entity.campaignType,
        adGroupId: entity.adGroupId,
        adGroupName: entity.adGroupName,
        biddingStrategyType: entity.biddingStrategyType,
        device: 'TOTAL',
        deviceName: 'Total',
        cost: totalCost,
        conversions: totalConversions,
        conversionsValue: totalConversionsValue,
        clicks: totalClicks,
        impressions: totalImpressions,
        cpa: entityAvgCpa,
        roas: entityAvgRoas,
        isTotal: true
      });
    });

    return rows;
  }, [recommendations]);

  // Filtrer et trier les données
  const filteredData = useMemo(() => {
    let result = [...flattenedData];

    // Filtre par recherche (nom d'entité)
    if (searchFilter.trim()) {
      const search = searchFilter.toLowerCase().trim();
      result = result.filter(item =>
        item.entityName?.toLowerCase().includes(search) ||
        item.campaignName?.toLowerCase().includes(search) ||
        item.adGroupName?.toLowerCase().includes(search)
      );
    }

    // Filtre par niveau (campagne/adgroup)
    if (levelFilter !== 'ALL') {
      result = result.filter(item => item.entityType === levelFilter);
    }

    // Filtre par type de campagne
    if (typeFilter !== 'ALL') {
      result = result.filter(item => item.campaignType === typeFilter);
    }

    // Filtre par stratégie d'enchères
    if (strategyFilter !== 'ALL') {
      result = result.filter(item => item.biddingStrategyType === strategyFilter);
    }

    // Filtre par type de recommandation
    if (recommendationFilter) {
      result = result.filter(item => {
        if (item.isTotal) return false;
        if (recommendationFilter === 'actionable') {
          return ['increase', 'decrease', 'exclusion'].includes(item.recommendationType);
        }
        if (recommendationFilter === 'not_applicable') {
          return item.recommendationType === 'not_applicable';
        }
        if (recommendationFilter === 'insufficient_data') {
          return item.recommendationType === 'insufficient_data';
        }
        return item.recommendationType === recommendationFilter;
      });
    }

    // Filtre par appareil
    if (deviceFilter) {
      result = result.filter(item => item.device === deviceFilter || item.isTotal);
    }

    // Calculer le coût total par entité pour le tri
    const entityCostMap = {};
    result.forEach(item => {
      if (item.isTotal) {
        entityCostMap[item.entityId] = item.cost;
      }
    });

    // Tri
    result.sort((a, b) => {
      // D'abord par coût total d'entité (décroissant)
      const costA = entityCostMap[a.entityId] || 0;
      const costB = entityCostMap[b.entityId] || 0;
      if (costA !== costB) return costB - costA;

      // Garder les lignes d'une même entité ensemble
      if (a.entityId !== b.entityId) return 0;

      // Ordre des appareils
      const deviceOrder = { 'DESKTOP': 0, 'MOBILE': 1, 'TABLET': 2, 'CONNECTED_TV': 3, 'OTHER': 4, 'TOTAL': 100 };
      const deviceA = deviceOrder[a.device] ?? 99;
      const deviceB = deviceOrder[b.device] ?? 99;
      return deviceA - deviceB;
    });

    return result;
  }, [flattenedData, searchFilter, levelFilter, typeFilter, strategyFilter, recommendationFilter, deviceFilter]);

  // Formatage des nombres (avec 1 décimale pour les conversions)
  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '-';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num.toFixed(1);
  };

  const formatInteger = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '-';
    if (num >= 1000000) return Math.round(num / 1000000) + 'M';
    if (num >= 1000) return Math.round(num / 1000) + 'k';
    return Math.round(num).toString();
  };

  const formatCurrency = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '-';
    return num.toFixed(1) + '€';
  };

  const formatCPC = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '-';
    return num.toFixed(2) + '€';
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '-';
    return (num * 100).toFixed(0) + '%';
  };

  // Badge de recommandation avec ajustement quantitatif
  const getRecommendationBadge = (item, entityName = '', entityType = 'campaign') => {
    const { recommendationType, adjustmentPercent, finalAdjustmentPercent, canApply, entityId, device, campaignType, biddingStrategyType } = item;
    const applyKey = `${entityId}-${device}`;
    const isApplying = applyingAdjustments.has(applyKey);
    const isApplied = appliedAdjustments.has(applyKey);

    // Performance Max : ajustements d'enchères non disponibles
    if (campaignType === 'PERFORMANCE_MAX') {
      return (
        <span style={{ fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>
          Ajustements d'enchères indisponibles
        </span>
      );
    }

    // TARGET_ROAS, MAXIMIZE_CONVERSION_VALUE et TARGET_IMPRESSION_SHARE : seul -100% est possible
    if (biddingStrategyType === 'TARGET_ROAS' || biddingStrategyType === 'MAXIMIZE_CONVERSION_VALUE' || biddingStrategyType === 'TARGET_IMPRESSION_SHARE') {
      const customValueRoas = getCustomAdjustment(entityId, device);
      const hasCustomValueRoas = customValueRoas !== undefined && customValueRoas !== null && customValueRoas !== '';

      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#f3f4f6',
              borderRadius: '4px',
              border: hasCustomValueRoas ? '2px solid #1a1a1a' : '1px solid transparent',
              overflow: 'hidden'
            }}>
              <input
                type="text"
                value={hasCustomValueRoas ? '-100' : ''}
                onChange={(e) => {
                  // Dès qu'on entre quelque chose, on met -100
                  if (e.target.value !== '') {
                    setCustomAdjustment(entityId, device, -100);
                  } else {
                    setCustomAdjustment(entityId, device, '');
                  }
                }}
                onFocus={(e) => {
                  // Dès qu'on focus, on met -100
                  setCustomAdjustment(entityId, device, -100);
                }}
                placeholder=""
                style={{
                  width: '60px',
                  padding: '4px 6px',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: hasCustomValueRoas ? '#dc2626' : '#9ca3af',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  textAlign: 'right'
                }}
              />
              <span style={{
                paddingRight: '8px',
                fontSize: '18px',
                fontWeight: '700',
                color: hasCustomValueRoas ? '#dc2626' : '#9ca3af'
              }}>%</span>
            </div>
            {hasCustomValueRoas && (
              <button
                onClick={() => resetCustomAdjustment(entityId, device)}
                style={{
                  padding: '4px 6px',
                  fontSize: '10px',
                  color: '#6b7280',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                title="Réinitialiser"
              >
                Reset
              </button>
            )}
            {hasCustomValueRoas && (
              <button
                onClick={() => applyAdjustment(entityId, entityType, device, -100, entityName)}
                disabled={isApplying || isApplied}
                style={{
                  padding: '4px 10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: isApplied ? '#166534' : 'white',
                  backgroundColor: isApplied ? '#dcfce7' : isApplying ? '#9ca3af' : '#333333',
                  border: isApplied ? '1px solid #86efac' : 'none',
                  borderRadius: '4px',
                  cursor: isApplying || isApplied ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title={isApplied ? 'Ajustement appliqué' : 'Appliquer -100% dans Google Ads'}
              >
                {isApplying ? (
                  <>
                    <span style={{
                      display: 'inline-block',
                      width: '10px',
                      height: '10px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></span>
                    En cours...
                  </>
                ) : isApplied ? (
                  <>
                    <Check size={12} />
                    Appliqué
                  </>
                ) : (
                  'Appliquer'
                )}
              </button>
            )}
          </div>
          <span style={{ fontSize: '9px', color: '#6b7280', fontStyle: 'italic' }}>
            Seul -100% possible avec {biddingStrategyType === 'TARGET_ROAS' ? 'ROAS cible' : biddingStrategyType === 'TARGET_IMPRESSION_SHARE' ? 'Taux d\'impressions cible' : 'Maximiser la valeur de conversion'}
          </span>
        </div>
      );
    }

    // Afficher l'ajustement si applicable
    const originalPercent = canApply ? finalAdjustmentPercent : adjustmentPercent;
    const customValue = getCustomAdjustment(entityId, device);
    const hasCustomValue = customValue !== undefined && customValue !== null;
    const displayPercent = hasCustomValue ? customValue : originalPercent;

    // Pour données insuffisantes - permettre quand même la modification manuelle
    if (recommendationType === 'insufficient_data') {
      const customValueInsuff = getCustomAdjustment(entityId, device);
      const hasCustomValueInsuff = customValueInsuff !== undefined && customValueInsuff !== null;
      const displayPercentInsuff = hasCustomValueInsuff ? customValueInsuff : 0;

      // Couleur selon la valeur personnalisée
      const numericPercentInsuff = typeof displayPercentInsuff === 'number' ? displayPercentInsuff : (parseInt(displayPercentInsuff, 10) || 0);
      let colorInsuff = '#9ca3af';
      let bgColorInsuff = '#f3f4f6';
      let borderColorInsuff = 'transparent';
      if (hasCustomValueInsuff && displayPercentInsuff !== '' && displayPercentInsuff !== '-') {
        if (numericPercentInsuff > 0) {
          colorInsuff = '#166534';
          bgColorInsuff = '#dcfce7';
        } else if (numericPercentInsuff < 0) {
          colorInsuff = '#991b1b';
          bgColorInsuff = '#fee2e2';
        }
        borderColorInsuff = '#1a1a1a';
      }

      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: bgColorInsuff,
              borderRadius: '4px',
              border: `2px solid ${borderColorInsuff}`,
              overflow: 'hidden'
            }}>
              <input
                type="text"
                value={hasCustomValueInsuff && displayPercentInsuff !== '' && displayPercentInsuff !== 0 ? `${numericPercentInsuff > 0 ? '+' : ''}${displayPercentInsuff}` : ''}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9-]/g, '');
                  setCustomAdjustment(entityId, device, val);
                }}
                onBlur={(e) => {
                  const val = e.target.value.replace(/[^0-9-]/g, '');
                  setCustomAdjustment(entityId, device, val, true);
                }}
                onFocus={(e) => { e.target.value = ''; setCustomAdjustment(entityId, device, ''); }}
                placeholder=""
                className="adjustment-input"
                style={{
                  width: '60px',
                  padding: '4px 6px',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: colorInsuff,
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  textAlign: 'right'
                }}
              />
              <span style={{
                paddingRight: '8px',
                fontSize: '18px',
                fontWeight: '700',
                color: colorInsuff
              }}>%</span>
            </div>
            {hasCustomValueInsuff && (
              <button
                onClick={() => resetCustomAdjustment(entityId, device)}
                style={{
                  padding: '4px 6px',
                  fontSize: '10px',
                  color: '#6b7280',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                title="Réinitialiser"
              >
                Reset
              </button>
            )}
            {/* Bouton Appliquer pour données insuffisantes */}
            {hasCustomValueInsuff && displayPercentInsuff !== '' && (
              <button
                onClick={() => applyAdjustment(entityId, entityType, device, numericPercentInsuff, entityName)}
                disabled={isApplying || isApplied}
                style={{
                  padding: '4px 10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: isApplied ? '#166534' : 'white',
                  backgroundColor: isApplied ? '#dcfce7' : isApplying ? '#9ca3af' : '#333333',
                  border: isApplied ? '1px solid #86efac' : 'none',
                  borderRadius: '4px',
                  cursor: isApplying || isApplied ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title={isApplied ? 'Ajustement appliqué' : 'Appliquer cet ajustement dans Google Ads'}
              >
                {isApplying ? (
                  <>
                    <span style={{
                      display: 'inline-block',
                      width: '10px',
                      height: '10px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></span>
                    En cours...
                  </>
                ) : isApplied ? (
                  <>
                    <Check size={12} />
                    Appliqué
                  </>
                ) : (
                  'Appliquer'
                )}
              </button>
            )}
          </div>
          <span style={{ fontSize: '10px', color: '#9ca3af', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '4px' }}>
            Données insuff.
            <HelpCircle
              style={{ width: '12px', height: '12px', cursor: 'help' }}
              title="Moins de 5 conversions pondérées sur la période."
            />
          </span>
        </div>
      );
    }

    if (recommendationType === 'not_applicable') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{
            fontSize: '12px',
            fontWeight: '500',
            color: '#92400e'
          }}>
            Non applicable
          </span>
          {adjustmentPercent !== null && (
            <span style={{ fontSize: '10px', color: '#92400e', fontStyle: 'italic' }}>
              (suggéré: {adjustmentPercent > 0 ? '+' : ''}{Math.round(adjustmentPercent)}%)
            </span>
          )}
        </div>
      );
    }

    // Déterminer la couleur selon le type
    const numericPercent = typeof displayPercent === 'number' ? displayPercent : (parseInt(displayPercent, 10) || 0);
    let color = '#374151';
    let bgColor = '#f3f4f6';
    let borderColor = 'transparent';
    if (numericPercent > 0) {
      color = '#166534';
      bgColor = '#dcfce7';
      borderColor = '#86efac';
    } else if (numericPercent < 0) {
      color = '#991b1b';
      bgColor = '#fee2e2';
      borderColor = '#fca5a5';
    }

    // Exclusion (-100%)
    if (numericPercent === -100) {
      color = '#ffffff';
      bgColor = '#7f1d1d';
      borderColor = '#7f1d1d';
    }

    // Style modifié par l'utilisateur
    if (hasCustomValue) {
      borderColor = '#1a1a1a';
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: bgColor,
            borderRadius: '4px',
            border: `2px solid ${borderColor}`,
            overflow: 'hidden'
          }}>
            <input
              type="text"
              value={displayPercent !== null && displayPercent !== undefined && displayPercent !== '' ? `${numericPercent > 0 ? '+' : ''}${typeof displayPercent === 'number' ? Math.round(displayPercent) : displayPercent}` : ''}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9-]/g, '');
                setCustomAdjustment(entityId, device, val);
              }}
              onBlur={(e) => {
                const val = e.target.value.replace(/[^0-9-]/g, '');
                setCustomAdjustment(entityId, device, val, true);
              }}
              onFocus={(e) => { e.target.value = ''; setCustomAdjustment(entityId, device, ''); }}
              placeholder=""
              className="adjustment-input"
              style={{
                width: '60px',
                padding: '4px 6px',
                fontSize: '18px',
                fontWeight: '700',
                color: color,
                backgroundColor: 'transparent',
                border: 'none',
                outline: 'none',
                textAlign: 'right'
              }}
            />
            <span style={{
              paddingRight: '8px',
              fontSize: '18px',
              fontWeight: '700',
              color: color
            }}>%</span>
          </div>
          {hasCustomValue && (
            <button
              onClick={() => resetCustomAdjustment(entityId, device)}
              style={{
                padding: '4px 6px',
                fontSize: '10px',
                color: '#6b7280',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              title="Réinitialiser à la valeur recommandée"
            >
              Reset
            </button>
          )}
          {/* Bouton Appliquer */}
          {canApply && displayPercent !== null && displayPercent !== undefined && displayPercent !== '' && (
            <button
              onClick={() => applyAdjustment(entityId, entityType, device, numericPercent, entityName)}
              disabled={isApplying || isApplied}
              style={{
                padding: '4px 10px',
                fontSize: '13px',
                fontWeight: '600',
                color: isApplied ? '#166534' : 'white',
                backgroundColor: isApplied ? '#dcfce7' : isApplying ? '#9ca3af' : '#333333',
                border: isApplied ? '1px solid #86efac' : 'none',
                borderRadius: '4px',
                cursor: isApplying || isApplied ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              title={isApplied ? 'Ajustement appliqué' : 'Appliquer cet ajustement dans Google Ads'}
            >
              {isApplying ? (
                <>
                  <span style={{
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  En cours...
                </>
              ) : isApplied ? (
                <>
                  <Check size={12} />
                  Appliqué
                </>
              ) : (
                'Appliquer'
              )}
            </button>
          )}
        </div>
        {hasCustomValue && originalPercent !== null && (
          <span style={{ fontSize: '9px', color: '#6b7280', fontStyle: 'italic' }}>
            Reco: {originalPercent > 0 ? '+' : ''}{Math.round(originalPercent)}%
          </span>
        )}
      </div>
    );
  };

  // Mini graphe SVG pour le tooltip confiance
  const renderMiniChart = (dailyChartData, referenceValue, referenceMetric) => {
    if (!dailyChartData || dailyChartData.length === 0) return null;

    const width = 580;
    const height = 120;
    const padding = { top: 15, right: 20, bottom: 25, left: 45 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Calculer les max pour l'échelle
    const maxConversions = Math.max(...dailyChartData.map(d => d.conversions), 1);

    // Calculer CPA quotidien pour la courbe
    const dailyWithCpa = dailyChartData.map(d => ({
      ...d,
      cpa: d.conversions > 0 ? d.cost / d.conversions : null
    }));

    const cpasWithValues = dailyWithCpa.filter(d => d.cpa !== null).map(d => d.cpa);
    const maxCpa = cpasWithValues.length > 0 ? Math.max(...cpasWithValues) : referenceValue || 100;
    const minCpa = cpasWithValues.length > 0 ? Math.min(...cpasWithValues) : 0;
    const cpaRange = maxCpa - minCpa || 1;

    // Barres de conversions
    const barWidth = Math.max(1, (chartWidth / dailyChartData.length) - 1);

    // Générer les points de la courbe CPA
    const cpaPoints = dailyWithCpa
      .map((d, i) => {
        if (d.cpa === null) return null;
        const x = padding.left + (i / dailyChartData.length) * chartWidth + barWidth / 2;
        const y = padding.top + chartHeight - ((d.cpa - minCpa) / cpaRange) * chartHeight;
        return `${x},${y}`;
      })
      .filter(p => p !== null)
      .join(' ');

    // Ligne de référence (objectif CPA)
    const refY = referenceValue && referenceMetric === 'CPA'
      ? padding.top + chartHeight - ((referenceValue - minCpa) / cpaRange) * chartHeight
      : null;

    return (
      <svg width={width} height={height} style={{ display: 'block', marginBottom: '8px' }}>
        {/* Axe Y gauche (conversions) */}
        <text x={padding.left - 5} y={padding.top + 5} fontSize="10" fill="#9ca3af" textAnchor="end">{maxConversions.toFixed(0)}</text>
        <text x={padding.left - 5} y={height - padding.bottom} fontSize="10" fill="#9ca3af" textAnchor="end">0</text>

        {/* Barres de conversions */}
        {dailyChartData.map((d, i) => {
          const barHeight = (d.conversions / maxConversions) * chartHeight;
          const x = padding.left + (i / dailyChartData.length) * chartWidth;
          const y = padding.top + chartHeight - barHeight;
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              fill="#60a5fa"
              opacity={0.6}
            />
          );
        })}

        {/* Courbe CPA */}
        {cpaPoints && (
          <polyline
            points={cpaPoints}
            fill="none"
            stroke="#f97316"
            strokeWidth="1.5"
          />
        )}

        {/* Ligne objectif CPA */}
        {refY && refY >= padding.top && refY <= height - padding.bottom && (
          <>
            <line
              x1={padding.left}
              y1={refY}
              x2={width - padding.right}
              y2={refY}
              stroke="#22c55e"
              strokeWidth="1.5"
              strokeDasharray="5,3"
            />
            <text x={width - padding.right + 4} y={refY + 4} fontSize="10" fill="#22c55e">Obj.</text>
          </>
        )}

        {/* Légende */}
        <rect x={padding.left} y={height - 14} width={10} height={10} fill="#60a5fa" opacity={0.6} />
        <text x={padding.left + 14} y={height - 5} fontSize="10" fill="#9ca3af">Conv.</text>
        <line x1={padding.left + 60} y1={height - 9} x2={padding.left + 80} y2={height - 9} stroke="#f97316" strokeWidth="2" />
        <text x={padding.left + 84} y={height - 5} fontSize="10" fill="#9ca3af">CPA</text>
        {referenceValue && referenceMetric === 'CPA' && (
          <>
            <line x1={padding.left + 130} y1={height - 9} x2={padding.left + 150} y2={height - 9} stroke="#22c55e" strokeWidth="1.5" strokeDasharray="4,2" />
            <text x={padding.left + 154} y={height - 5} fontSize="10" fill="#9ca3af">Objectif</text>
          </>
        )}
      </svg>
    );
  };

  // Tableau des métriques par fenêtre
  const renderWindowMetricsTable = (windowMetrics) => {
    if (!windowMetrics) return null;

    const windows = [7, 14, 30, 60, 90];
    const cellStyle = { padding: '6px 10px', fontSize: '12px', textAlign: 'right', borderBottom: '1px solid #374151' };
    const headerStyle = { ...cellStyle, fontWeight: '600', color: '#93c5fd', textAlign: 'center', fontSize: '11px' };

    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #4b5563' }}>
            <th style={{ ...headerStyle, textAlign: 'left' }}>Jours</th>
            <th style={headerStyle}>Clics</th>
            <th style={headerStyle}>% conv.</th>
            <th style={headerStyle}>Conv.</th>
            <th style={headerStyle}>CPC</th>
            <th style={headerStyle}>Coût</th>
            <th style={headerStyle}>CPA</th>
            <th style={headerStyle}>ROAS</th>
          </tr>
        </thead>
        <tbody>
          {windows.map(days => {
            const m = windowMetrics[days];
            if (!m) return null;
            return (
              <tr key={days}>
                <td style={{ ...cellStyle, textAlign: 'left', color: '#d1d5db' }}>{days}j</td>
                <td style={cellStyle}>{m.clicks?.toLocaleString() || '—'}</td>
                <td style={cellStyle}>{m.convRate !== null ? `${m.convRate.toFixed(2)}%` : '—'}</td>
                <td style={cellStyle}>{m.conversions !== null ? m.conversions.toFixed(1) : '—'}</td>
                <td style={cellStyle}>{m.cpc !== null ? `${m.cpc.toFixed(2)}€` : '—'}</td>
                <td style={cellStyle}>{m.cost !== null ? `${m.cost.toFixed(0)}€` : '—'}</td>
                <td style={cellStyle}>{m.cpa !== null ? `${m.cpa.toFixed(1)}€` : '—'}</td>
                <td style={cellStyle}>{m.roas !== null ? `${(m.roas * 100).toFixed(0)}%` : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  // Badge de confiance
  const getConfidenceBadge = (item) => {
    const { confidenceLevel, confidenceScore, lookbackWindow, windowMetrics, dailyChartData, referenceValue, referenceMetric } = item;

    if (!confidenceLevel || item.isTotal) {
      return <span style={{ color: '#9ca3af' }}>—</span>;
    }

    const style = CONFIDENCE_STYLES[confidenceLevel] || CONFIDENCE_STYLES.INSUFFICIENT;

    const handleMouseEnter = (e) => {
      const tooltipWidth = 620;
      const tooltipHeight = 380;
      // Centrer le tooltip sur l'écran
      const x = (window.innerWidth - tooltipWidth) / 2;
      const y = (window.innerHeight - tooltipHeight) / 2;

      setTooltipPosition({ x, y, visible: true, item });
    };

    const handleMouseLeave = () => {
      setTooltipPosition(prev => ({ ...prev, visible: false }));
    };

    return (
      <div
        className="confidence-cell"
        style={{ position: 'relative' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span style={{
          padding: '2px 8px',
          fontSize: '11px',
          fontWeight: '500',
          borderRadius: '4px',
          backgroundColor: style.bg,
          color: style.color,
          cursor: 'help'
        }}>
          {style.label}
        </span>
      </div>
    );
  };

  // Badge de type de campagne
  const getCampaignTypeBadge = (type) => {
    const style = CHANNEL_BADGE_STYLES[type] || { bg: '#f3f4f6', color: '#6b7280', label: type };

    return (
      <span style={{
        padding: '2px 6px',
        fontSize: '10px',
        fontWeight: '600',
        borderRadius: '4px',
        backgroundColor: style.bg,
        color: style.color
      }}>
        {style.label}
      </span>
    );
  };

  // Icône d'appareil (monochrome simple)
  const getDeviceIcon = (device, deviceName) => {
    // Essayer d'abord avec le code brut, puis avec le nom
    const IconComponent = DEVICE_ICONS[device] || DEVICE_ICONS[deviceName] || HelpCircle;
    return <IconComponent size={14} strokeWidth={2.5} style={{ color: '#374151', flexShrink: 0 }} />;
  };

  return (
    <div style={{ width: '100%', padding: '0' }}>
      {/* En-tête */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827',
              display: 'flex',
              alignItems: 'center',
              margin: '0 0 8px 0'
            }}>
              <Smartphone style={{ marginRight: '8px', height: '24px', width: '24px', color: '#333333' }} />
              Ajusteur d'Enchères par Appareils
            </h2>
            <p style={{ color: '#6b7280', margin: '0', fontSize: '16px' }}>
              Recommandations d'ajustement d'enchères par appareil (Mobile, Desktop, Tablette, TV)
            </p>
          </div>
          {selectedAccount && (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  border: hasCustomSettings && !showFilters ? '1px solid #16a34a' : '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: showFilters ? '#f3f4f6' : hasCustomSettings ? '#f0fdf4' : 'white',
                  color: hasCustomSettings && !showFilters ? '#166534' : '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                <Settings style={{ height: '16px', width: '16px', marginRight: '8px' }} />
                {hasCustomSettings ? 'Paramètres personnalisés' : 'Paramètres'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sélecteur de compte */}
      {/* Sans compte, la carte du sélecteur n'a rien à encadrer : on affiche
          l'état vide seul, plutôt que de le loger dans un cadre dans un cadre. */}
      {!loadingAccounts && accounts.length === 0 ? (
        <AucunCompteGoogleAds token={token} />
      ) : (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#111827', margin: '0 0 12px 0' }}>
            Compte Google Ads
          </h3>

          {loadingAccounts ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{
                display: 'inline-block',
                width: '20px',
                height: '20px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #333333',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ margin: '8px 0 0 0', color: '#6b7280' }}>Chargement des comptes...</p>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', minWidth: '100px' }}>
                Sélectionner :
              </label>
              <select
                value={selectedAccount}
                onChange={(e) => {
                  setSelectedAccount(e.target.value);
                  setRecommendations([]);
                  setSummary(null);
                  setCached(false);
                  if (updateUrlWithAccount) updateUrlWithAccount(e.target.value || null);
                }}
                style={{
                  flex: '1',
                  minWidth: '300px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white'
                }}
              >
                <option value="">-- Choisir un compte --</option>
                {accounts.map(account => {
                  const accountId = account.sourceId || account.id;
                  const accountName = account.name || account.displayName;
                  const displayName = accountName.includes(accountId) ? accountName : `${accountName} (${accountId})`;
                  return (
                    <option key={accountId} value={accountId}>
                      {displayName}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Panneau de paramètres */}
      {showFilters && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '16px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          position: 'relative'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '500', color: '#111827', margin: '0' }}>
              Paramètres d'analyse
            </h3>
            <button
              onClick={() => { setSettings({...savedSettings}); setShowFilters(false); }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Fermer et annuler les modifications"
            >
              <X size={20} />
            </button>
          </div>
          <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'center' }}>
            <div style={{ flex: '0 0 160px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Période d'analyse
              </label>
              <select
                value={settings.displayPeriod}
                onChange={(e) => setSettings({...settings, displayPeriod: parseInt(e.target.value)})}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
              >
                <option value={1}>Aujourd'hui</option>
                <option value={3}>3 derniers jours</option>
                <option value={7}>7 derniers jours</option>
                <option value={14}>14 derniers jours</option>
                <option value={30}>30 derniers jours</option>
                <option value={60}>60 derniers jours</option>
                <option value={90}>90 derniers jours</option>
              </select>
            </div>

            <div style={{ flex: '0 0 160px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Agressivité
              </label>
              <select
                value={settings.aggressiveness}
                onChange={(e) => setSettings({...settings, aggressiveness: e.target.value})}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
              >
                <option value="conservative">Prudent (5-10%)</option>
                <option value="moderate">Modéré (10-20%)</option>
                <option value="aggressive">Agressif (15-30%)</option>
              </select>
            </div>

            <div style={{ flex: '0 0 160px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Conversions min.
              </label>
              <input
                type="number"
                value={settings.minConversions}
                onChange={(e) => setSettings({...settings, minConversions: parseFloat(e.target.value) || 5})}
                min="1"
                max="50"
                step="1"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
              />
              <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', marginBottom: '0' }}>
                Seuil min. pour recommandations
              </p>
            </div>

            <div style={{ flex: '0 0 180px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Clics sans conv.
              </label>
              <input
                type="number"
                min="0"
                max="1000"
                value={settings.minClicksNoConversion}
                onChange={(e) => setSettings({...settings, minClicksNoConversion: parseInt(e.target.value) || 0})}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                placeholder="100"
              />
              <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', marginBottom: '0' }}>
                Alerte si 0 conv. (0 = désactivé)
              </p>
            </div>

            <div style={{ flex: '0 0 180px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Coût sans conv. (€)
              </label>
              <input
                type="number"
                min="0"
                max="10000"
                value={settings.minCostNoConversion}
                onChange={(e) => setSettings({...settings, minCostNoConversion: parseInt(e.target.value) || 0})}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                placeholder="50"
              />
              <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', marginBottom: '0' }}>
                Alerte si 0 conv. (0 = désactivé)
              </p>
            </div>

            <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-start', paddingTop: '20px' }}>
              <button
                onClick={() => { setSavedSettings({...settings}); fetchData(); setShowFilters(false); }}
                disabled={!selectedAccount || !hasUnsavedChanges}
                style={{
                  padding: '8px 20px',
                  background: (selectedAccount && hasUnsavedChanges) ? '#333333' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (selectedAccount && hasUnsavedChanges) ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                  height: '38px'
                }}
              >
                Sauvegarder les paramètres
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          color: '#991b1b'
        }}>
          <strong>Erreur :</strong> {error}
        </div>
      )}

      {/* Chargement */}
      {selectedAccount && loading && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '60px 24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'inline-block',
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #333333',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>
            Analyse en cours...
          </h3>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: '0' }}>
            Récupération des performances par appareil
          </p>
        </div>
      )}

      {/* Statistiques */}
      {selectedAccount && !loading && summary && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '12px',
          marginBottom: '24px'
        }}>
          <div
            onClick={() => { setRecommendationFilter(null); setDeviceFilter(null); }}
            style={{
              background: !recommendationFilter && !deviceFilter ? '#f0f9ff' : 'white',
              borderRadius: '12px',
              padding: '16px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              border: !recommendationFilter && !deviceFilter ? '2px solid #333333' : '2px solid transparent',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ padding: '8px', background: '#dbeafe', borderRadius: '8px', marginRight: '12px' }}>
                <BarChart3 style={{ height: '18px', width: '18px', color: '#333333' }} />
              </div>
              <div>
                <p style={{ fontSize: '11px', fontWeight: '500', color: '#6b7280', margin: '0' }}>Total</p>
                <p style={{ fontSize: '22px', fontWeight: '600', color: '#111827', margin: '0' }}>{summary.totalRecommendations || summary.total || 0}</p>
              </div>
            </div>
          </div>

          <div
            onClick={() => { setRecommendationFilter(recommendationFilter === 'actionable' ? null : 'actionable'); setDeviceFilter(null); }}
            style={{
              background: recommendationFilter === 'actionable' ? '#dcfce7' : 'white',
              borderRadius: '12px',
              padding: '16px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              border: recommendationFilter === 'actionable' ? '2px solid #16a34a' : '2px solid transparent',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ padding: '8px', background: '#dcfce7', borderRadius: '8px', marginRight: '12px' }}>
                <TrendingUp style={{ height: '18px', width: '18px', color: '#16a34a' }} />
              </div>
              <div>
                <p style={{ fontSize: '11px', fontWeight: '500', color: '#6b7280', margin: '0' }}>Actionnables</p>
                <p style={{ fontSize: '22px', fontWeight: '600', color: '#16a34a', margin: '0' }}>{summary.actionable || 0}</p>
              </div>
            </div>
          </div>

          <div
            onClick={() => { setRecommendationFilter(recommendationFilter === 'not_applicable' ? null : 'not_applicable'); setDeviceFilter(null); }}
            style={{
              background: recommendationFilter === 'not_applicable' ? '#fef3c7' : 'white',
              borderRadius: '12px',
              padding: '16px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              border: recommendationFilter === 'not_applicable' ? '2px solid #d97706' : '2px solid transparent',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ padding: '8px', background: '#fef3c7', borderRadius: '8px', marginRight: '12px' }}>
                <AlertTriangle style={{ height: '18px', width: '18px', color: '#d97706' }} />
              </div>
              <div>
                <p style={{ fontSize: '11px', fontWeight: '500', color: '#6b7280', margin: '0' }}>Non applicable</p>
                <p style={{ fontSize: '22px', fontWeight: '600', color: '#d97706', margin: '0' }}>{summary.informational || 0}</p>
              </div>
            </div>
          </div>

          <div
            onClick={() => { setRecommendationFilter(recommendationFilter === 'insufficient_data' ? null : 'insufficient_data'); setDeviceFilter(null); }}
            style={{
              background: recommendationFilter === 'insufficient_data' ? '#f3f4f6' : 'white',
              borderRadius: '12px',
              padding: '16px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              border: recommendationFilter === 'insufficient_data' ? '2px solid #6b7280' : '2px solid transparent',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ padding: '8px', background: '#f3f4f6', borderRadius: '8px', marginRight: '12px' }}>
                <Info style={{ height: '18px', width: '18px', color: '#6b7280' }} />
              </div>
              <div>
                <p style={{ fontSize: '11px', fontWeight: '500', color: '#6b7280', margin: '0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Données insuff.
                  <HelpCircle
                    style={{ width: '12px', height: '12px', cursor: 'help' }}
                    title="Moins de 5 conversions pondérées sur la période."
                  />
                </p>
                <p style={{ fontSize: '22px', fontWeight: '600', color: '#6b7280', margin: '0' }}>{summary.insufficientData || 0}</p>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* Tableau des recommandations */}
      {selectedAccount && !loading && recommendations.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '500', color: '#111827', margin: '0' }}>
              {settings.level === 'hierarchical' ? (
                <>
                  <Layers style={{ display: 'inline', width: '18px', height: '18px', marginRight: '8px', verticalAlign: 'middle' }} />
                  Vue Hiérarchique
                  <span style={{ fontSize: '12px', fontWeight: '400', color: '#6b7280', marginLeft: '12px' }}>
                    {[...new Set(recommendations.map(r => r.entityId))].length} campagnes • {adgroupRecommendations.length > 0 ? [...new Set(adgroupRecommendations.map(r => r.entityId))].length : 0} adgroups
                  </span>
                </>
              ) : (
                <>Recommandations ({recommendations.length} {settings.level === 'campaign' ? 'campagnes' : 'groupes'})</>
              )}
              <span style={{ fontSize: '12px', fontWeight: '400', color: '#6b7280', marginLeft: '12px' }}>
                Métriques sur {settings.displayPeriod} jours
              </span>
            </h3>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  style={{
                    padding: '6px 10px 6px 32px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px',
                    width: '160px'
                  }}
                />
                <Search style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', width: '14px', height: '14px', color: '#9ca3af' }} />
              </div>
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: levelFilter !== 'ALL' ? '#dbeafe' : 'white' }}
              >
                <option value="ALL">Tous niveaux</option>
                <option value="campaign">Campagnes</option>
                <option value="adgroup">Adgroups</option>
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: typeFilter !== 'ALL' ? '#dbeafe' : 'white' }}
              >
                <option value="ALL">Tous types</option>
                {availableCampaignTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              <select
                value={strategyFilter}
                onChange={(e) => setStrategyFilter(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: strategyFilter !== 'ALL' ? '#dbeafe' : 'white' }}
              >
                <option value="ALL">Toutes stratégies</option>
                {availableBiddingStrategies.map(strategy => (
                  <option key={strategy.value} value={strategy.value}>{strategy.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="device-table-container" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0, zIndex: 10 }}>
                <tr>
                  <th
                    onClick={() => handleSort('name')}
                    style={{ padding: '10px 12px', textAlign: 'left', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb', minWidth: '200px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    {settings.level === 'hierarchical' ? 'Campagne / Groupe d\'annonces' : (settings.level === 'campaign' ? 'Campagne' : 'Groupe d\'annonces')}
                    <SortIcon column="name" />
                  </th>
                  <th style={{ padding: '10px 8px', textAlign: 'center', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '90px' }}>
                    Appareil
                  </th>
                  <th
                    onClick={() => handleSort('clicks')}
                    style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '60px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    Clics<SortIcon column="clicks" />
                  </th>
                  <th
                    onClick={() => handleSort('convRate')}
                    style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '60px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    % conv.<SortIcon column="convRate" />
                  </th>
                  <th
                    onClick={() => handleSort('conversions')}
                    style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '70px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    Conv.<SortIcon column="conversions" />
                  </th>
                  <th
                    onClick={() => handleSort('cpc')}
                    style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '60px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    CPC<SortIcon column="cpc" />
                  </th>
                  <th
                    onClick={() => handleSort('cost')}
                    style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '80px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    Coût<SortIcon column="cost" />
                  </th>
                  <th
                    onClick={() => handleSort('cpa')}
                    style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '60px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    CPA<SortIcon column="cpa" />
                  </th>
                  <th
                    onClick={() => handleSort('roas')}
                    style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '60px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    ROAS<SortIcon column="roas" />
                  </th>
                  <th style={{ padding: '10px 8px', textAlign: 'center', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '70px' }}>
                    Ajust. actuel
                  </th>
                  <th className="lastmodif-header" style={{ padding: '10px 8px', textAlign: 'center', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '85px', position: 'relative' }}>
                    Dernière modif.
                    <div className="lastmodif-header-tooltip">
                      <strong>Date de dernière modification</strong><br/><br/>
                      Affiche la date du dernier changement<br/>
                      d'ajustement d'enchère pour cet appareil.<br/><br/>
                      <span style={{ fontSize: '11px', color: '#fbbf24' }}>
                        ⚠️ Limitation API Google Ads :<br/>
                        Historique limité aux 30 derniers jours.
                      </span>
                    </div>
                  </th>
                  <th className="recommendation-header" style={{ padding: '10px 12px', textAlign: 'left', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '160px', position: 'relative' }}>
                    Ajustement<br/>recommandé
                    <div className="recommendation-header-tooltip">
                      <strong>Méthode de calcul</strong><br/><br/>
                      <span style={{ color: '#93c5fd' }}>1. Analyse des données</span><br/>
                      Fenêtre progressive (7→14→30→60→90j)<br/>
                      jusqu'à atteindre {settings.minConversions} conv. pondérées<br/><br/>
                      <span style={{ color: '#93c5fd' }}>2. Pondération time-decay</span><br/>
                      Données récentes = poids plus fort<br/>
                      (λ={settings.lambda}, demi-vie ≈ {Math.round(Math.log(2) / settings.lambda)}j)<br/><br/>
                      <span style={{ color: '#93c5fd' }}>3. Calcul de l'ajustement brut</span><br/>
                      Compare l'efficacité du device<br/>
                      à la moyenne de l'entité (CPA/ROAS)<br/><br/>
                      <span style={{ color: '#93c5fd' }}>4. Modération par la confiance</span><br/>
                      Ajust. final = Ajust. brut × Score confiance<br/>
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                        Moins de données = recommandation<br/>
                        plus conservatrice
                      </span>
                    </div>
                  </th>
                  <th className="confidence-header" style={{ padding: '10px 8px', textAlign: 'center', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '80px', position: 'relative' }}>
                    Confiance
                    <div className="confidence-header-tooltip">
                      <strong>Indice de confiance</strong><br/><br/>
                      Basé sur :<br/>
                      • Volume conversions pondérées (60%)<br/>
                      • Fraîcheur des données (40%)<br/><br/>
                      Pondération time-decay : λ={settings.lambda}<br/>
                      (demi-vie ≈ {Math.round(Math.log(2) / settings.lambda)} jours)
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Vue Hiérarchique */}
                {settings.level === 'hierarchical' && sortedHierarchicalData.map((campaign) => {
                  const isExpanded = expandedCampaigns.has(campaign.campaignId);

                  // Filtrer les devices selon les filtres actifs
                  const campaignDevices = Object.entries(campaign.devices)
                    .filter(([deviceKey, rec]) => {
                      // Filtre par appareil
                      if (deviceFilter && deviceKey !== deviceFilter) return false;

                      // Filtre par type de recommandation
                      if (recommendationFilter) {
                        let recType = 'maintain';
                        if (rec.status === 'INSUFFICIENT_DATA') recType = 'insufficient_data';
                        else if (rec.status === 'NOT_SUPPORTED' || rec.status === 'INFORMATIONAL') recType = 'not_applicable';
                        else if (rec.finalAdjustmentPercent === -100) recType = 'exclusion';
                        else if (rec.finalAdjustmentPercent > 5) recType = 'increase';
                        else if (rec.finalAdjustmentPercent < -5) recType = 'decrease';

                        if (recommendationFilter === 'actionable') {
                          if (!['increase', 'decrease', 'exclusion'].includes(recType)) return false;
                        } else if (recType !== recommendationFilter) {
                          return false;
                        }
                      }
                      return true;
                    })
                    .sort(([a], [b]) => {
                      const orderA = DEVICE_ORDER.indexOf(a);
                      const orderB = DEVICE_ORDER.indexOf(b);
                      return (orderA === -1 ? 999 : orderA) - (orderB === -1 ? 999 : orderB);
                    });

                  // Si aucun device ne correspond aux filtres, ne pas afficher la campagne
                  if (campaignDevices.length === 0) return null;

                  // Calculer totaux campagne (sur les devices filtrés)
                  let campaignTotalCost = 0, campaignTotalConversions = 0, campaignTotalConversionsValue = 0, campaignTotalClicks = 0;
                  campaignDevices.forEach(([, rec]) => {
                    campaignTotalCost += rec.displayCost || 0;
                    campaignTotalConversions += rec.displayConversions || 0;
                    campaignTotalConversionsValue += rec.displayConversionsValue || 0;
                    campaignTotalClicks += rec.displayClicks || 0;
                  });
                  const campaignAvgCpa = campaignTotalConversions > 0 ? campaignTotalCost / campaignTotalConversions : 0;
                  const campaignAvgRoas = campaignTotalCost > 0 ? campaignTotalConversionsValue / campaignTotalCost : 0;

                  const hasActiveFilter = recommendationFilter || deviceFilter;
                  const deviceRowCount = hasActiveFilter ? campaignDevices.length : campaignDevices.length + 1; // +1 pour inclure la ligne Total

                  return (
                    <React.Fragment key={`campaign-${campaign.campaignId}`}>
                      {/* Lignes devices de la campagne */}
                      {campaignDevices.map(([deviceKey, rec], deviceIndex) => {
                        const isFirst = deviceIndex === 0;
                        const isLastDevice = deviceIndex === campaignDevices.length - 1;
                        const isExpanded = expandedCampaigns.has(campaign.campaignId);
                        const hasAdgroups = campaign.adgroups && campaign.adgroups.length > 0;
                        // Marge blanche entre campagnes : sur la dernière ligne device (avant Total ou fin de campagne)
                        // Si la campagne est déployée avec des adgroups, pas de bordure ici (les adgroups gèrent la séparation)
                        const isLastDeviceBeforeTotal = isLastDevice && !hasActiveFilter && !(isExpanded && hasAdgroups);
                        const isLastDeviceWithFilter = isLastDevice && hasActiveFilter && !(isExpanded && hasAdgroups);
                        const borderStyle = isLastDeviceWithFilter ? '16px solid white' : (isLastDeviceBeforeTotal ? '1px solid #e5e7eb' : 'none');

                        // Type de recommandation
                        let recommendationType = 'maintain';
                        if (rec.status === 'INSUFFICIENT_DATA') recommendationType = 'insufficient_data';
                        else if (rec.status === 'NOT_SUPPORTED' || rec.status === 'INFORMATIONAL') recommendationType = 'not_applicable';
                        else if (rec.finalAdjustmentPercent === -100) recommendationType = 'exclusion';
                        else if (rec.finalAdjustmentPercent > 5) recommendationType = 'increase';
                        else if (rec.finalAdjustmentPercent < -5) recommendationType = 'decrease';

                        const rowBgColor = recommendationType === 'increase' ? '#dcfce7'
                          : ['decrease', 'exclusion'].includes(recommendationType) ? '#fee2e2'
                          : 'white';

                        const item = {
                          ...rec,
                          device: deviceKey,
                          deviceName: DEVICE_NAMES[deviceKey] || deviceKey,
                          recommendationType,
                          adjustmentPercent: rec.rawAdjustmentPercent,
                          finalAdjustmentPercent: rec.finalAdjustmentPercent,
                          canApply: rec.canApply !== false,
                          constraintWarning: rec.constraintWarning,
                          reason: rec.reason,
                          clicks: rec.displayClicks || 0,
                          conversions: rec.displayConversions || 0,
                          cost: rec.displayCost || 0,
                          cpa: rec.displayCPA || 0,
                          roas: rec.displayROAS || 0,
                          cpc: rec.displayCPC || 0,
                          currentAdjustment: rec.currentAdjustment || 0,
                          lastModified: rec.lastModified || null,
                          lookbackWindow: rec.lookbackWindow,
                          confidenceLevel: rec.confidenceLevel,
                          confidenceScore: rec.confidenceScore,
                          campaignType: campaign.campaignType
                        };

                        return (
                          <tr key={`campaign-${campaign.campaignId}-${deviceKey}`} style={{ backgroundColor: rowBgColor }}>
                            {isFirst && (
                              <td rowSpan={deviceRowCount} style={{ padding: '0', border: 'none', borderBottom: (isExpanded && campaign.adgroups && campaign.adgroups.length > 0) ? 'none' : '16px solid white', verticalAlign: 'top', overflow: 'hidden', position: 'relative', height: '1px' }}>
                                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#f3f4f6' }}></div>
                                <div style={{ position: 'relative' }}>
                                  {/* Cadre bleu : nom de campagne */}
                                  <div style={{
                                    backgroundColor: '#dbeafe',
                                    padding: '10px 12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}>
                                    <button
                                      onClick={() => toggleCampaignExpand(campaign.campaignId, campaign.campaignType, campaign.campaignName)}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        padding: '2px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center'
                                      }}
                                      title={isExpanded ? 'Replier' : (campaign.campaignType === 'PERFORMANCE_MAX' ? 'Voir les groupes de composants' : 'Voir les groupes d\'annonces')}
                                    >
                                      {isExpanded ? (
                                        <ChevronDown style={{ width: '16px', height: '16px', color: '#333333' }} />
                                      ) : (
                                        <ChevronRight style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                                      )}
                                    </button>
                                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e40af' }}>
                                      {campaign.campaignName}
                                      {campaign.adgroupCount > 0 && (
                                        <span style={{ marginLeft: '6px', fontWeight: '400', color: '#6b7280' }}>
                                          ({campaign.adgroupCount} adgroup{campaign.adgroupCount > 1 ? 's' : ''})
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                  {/* Cadre bleu : stratégie */}
                                  <div style={{
                                    backgroundColor: '#dbeafe',
                                    padding: '8px 12px',
                                    color: '#1e40af',
                                    fontWeight: '600',
                                    fontSize: '12px'
                                  }}>
                                    Stratégie : {BIDDING_STRATEGY_LABELS[campaign.biddingStrategyType] || campaign.biddingStrategyType}
                                  </div>
                                  {/* Cadre gris : objectif + Personnaliser */}
                                    {customObjectives[campaign.campaignId] ? (
                                      <div style={{
                                        backgroundColor: '#f3f4f6',
                                        padding: '8px 12px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4px'
                                      }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          <span style={{ color: '#4b5563', fontWeight: '600', fontSize: '12px' }}>
                                            🎯 Objectif : {customObjectives[campaign.campaignId].objectiveType === 'CPA'
                                              ? `CPA ${formatCurrency(customObjectives[campaign.campaignId].objectiveValue)}`
                                              : `ROAS ${customObjectives[campaign.campaignId].objectiveValue.toFixed(0)}%`
                                            }
                                          </span>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              deleteCustomObjective(campaign.campaignId);
                                            }}
                                            style={{
                                              background: 'none',
                                              border: 'none',
                                              padding: '2px',
                                              cursor: 'pointer',
                                              color: '#ef4444',
                                              display: 'inline-flex',
                                              alignItems: 'center'
                                            }}
                                            title="Supprimer l'objectif personnalisé"
                                          >
                                            <X style={{ width: '12px', height: '12px' }} />
                                          </button>
                                        </div>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openObjectiveEditor(campaign.campaignId, campaign.campaignName, campaign.targetCpa, campaign.targetRoas);
                                          }}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: '2px 6px',
                                            cursor: 'pointer',
                                            color: '#6b7280',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            fontSize: '11px'
                                          }}
                                          title="Modifier l'objectif"
                                        >
                                          <Edit3 style={{ width: '12px', height: '12px' }} />
                                          Personnaliser
                                        </button>
                                      </div>
                                    ) : campaign.biddingStrategyType === 'MAXIMIZE_CONVERSION_VALUE' ? (
                                      campaignAvgRoas > 0 && (
                                        <div style={{
                                          backgroundColor: '#f3f4f6',
                                          padding: '8px 12px',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: '4px'
                                        }}>
                                          <span style={{ color: '#4b5563', fontWeight: '600', fontSize: '12px' }}>
                                            🎯 Objectif : ROAS {(campaignAvgRoas * 100).toFixed(0)}% moyen
                                          </span>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openObjectiveEditor(campaign.campaignId, campaign.campaignName, campaign.targetCpa, campaign.targetRoas);
                                            }}
                                            style={{
                                              background: 'none',
                                              border: 'none',
                                              padding: '2px 6px',
                                              cursor: 'pointer',
                                              color: '#6b7280',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                              fontSize: '11px'
                                            }}
                                            title="Définir un objectif personnalisé"
                                          >
                                            <Edit3 style={{ width: '12px', height: '12px' }} />
                                            Personnaliser
                                          </button>
                                        </div>
                                      )
                                    ) : campaign.biddingStrategyType === 'MAXIMIZE_CONVERSIONS' ? (
                                      campaignAvgCpa > 0 && (
                                        <div style={{
                                          backgroundColor: '#f3f4f6',
                                          padding: '8px 12px',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: '4px'
                                        }}>
                                          <span style={{ color: '#4b5563', fontWeight: '600', fontSize: '12px' }}>
                                            🎯 Objectif : CPA {formatCurrency(campaignAvgCpa)} moyen
                                          </span>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openObjectiveEditor(campaign.campaignId, campaign.campaignName, campaign.targetCpa, campaign.targetRoas);
                                            }}
                                            style={{
                                              background: 'none',
                                              border: 'none',
                                              padding: '2px 6px',
                                              cursor: 'pointer',
                                              color: '#6b7280',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                              fontSize: '11px'
                                            }}
                                            title="Définir un objectif personnalisé"
                                          >
                                            <Edit3 style={{ width: '12px', height: '12px' }} />
                                            Personnaliser
                                          </button>
                                        </div>
                                      )
                                    ) : ['MANUAL_CPC', 'MANUAL_CPM', 'MAXIMIZE_CLICKS'].includes(campaign.biddingStrategyType) ? (
                                      <div style={{
                                        backgroundColor: '#f3f4f6',
                                        padding: '8px 12px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4px'
                                      }}>
                                        {(campaignAvgCpa > 0 || campaignAvgRoas > 0) && (
                                          <span style={{ color: '#4b5563', fontWeight: '600', fontSize: '12px' }}>
                                            🎯 Objectif : {campaignAvgCpa > 0 && `CPA ${formatCurrency(campaignAvgCpa)}`}{campaignAvgCpa > 0 && campaignAvgRoas > 0 && ' et '}{campaignAvgRoas > 0 && `ROAS ${(campaignAvgRoas * 100).toFixed(0)}%`} moyens
                                          </span>
                                        )}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openObjectiveEditor(campaign.campaignId, campaign.campaignName, campaign.targetCpa, campaign.targetRoas);
                                          }}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: '2px 6px',
                                            cursor: 'pointer',
                                            color: '#6b7280',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            fontSize: '11px'
                                          }}
                                          title="Définir un objectif personnalisé"
                                        >
                                          <Edit3 style={{ width: '12px', height: '12px' }} />
                                          Personnaliser
                                        </button>
                                      </div>
                                    ) : (
                                      <div style={{
                                        backgroundColor: '#f3f4f6',
                                        padding: '8px 12px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4px'
                                      }}>
                                        {(campaign.targetCpa || campaign.targetRoas) && (
                                          <span style={{ color: '#4b5563', fontWeight: '600', fontSize: '12px' }}>
                                            🎯 Objectif : {campaign.targetCpa && `CPA ${formatCurrency(campaign.targetCpa)}`}{campaign.targetCpa && campaign.targetRoas && ' et '}{campaign.targetRoas && `ROAS ${(campaign.targetRoas * 100).toFixed(0)}%`}
                                          </span>
                                        )}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openObjectiveEditor(campaign.campaignId, campaign.campaignName, campaign.targetCpa, campaign.targetRoas);
                                          }}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: '2px 6px',
                                            cursor: 'pointer',
                                            color: '#6b7280',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            fontSize: '11px'
                                          }}
                                          title="Définir un objectif personnalisé"
                                        >
                                          <Edit3 style={{ width: '12px', height: '12px' }} />
                                          Personnaliser
                                        </button>
                                      </div>
                                    )}
                                    {/* Cadre jaune/vert : recommandation d'action */}
                                    {campaign.avgCV !== null && (
                                      (campaign.hasOutliers && campaign.adgroups && campaign.adgroups.length > 1) ? (
                                        <div
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedCampaigns(prev => {
                                              const newSet = new Set(prev);
                                              if (newSet.has(campaign.campaignId)) {
                                                newSet.delete(campaign.campaignId);
                                              } else {
                                                newSet.add(campaign.campaignId);
                                              }
                                              return newSet;
                                            });
                                          }}
                                          style={{
                                            backgroundColor: '#dc2626',
                                            padding: '8px 12px',
                                            color: '#ffffff',
                                            fontWeight: '600',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px'
                                          }}
                                        >
                                          <AlertTriangle style={{ width: '14px', height: '14px' }} />
                                          Agir sur les adgroups en priorité - cliquez ici
                                        </div>
                                      ) : (
                                        <div style={{
                                          backgroundColor: '#dcfce7',
                                          padding: '8px 12px',
                                          color: '#15803d',
                                          fontWeight: '600',
                                          fontSize: '12px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '6px'
                                        }}>
                                          <Check style={{ width: '14px', height: '14px' }} />
                                          Agir sur la campagne
                                        </div>
                                      )
                                    )}
                                </div>
                              </td>
                            )}
                            <td style={{ padding: '10px 8px', textAlign: 'center', borderBottom: borderStyle }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                                {getDeviceIcon(deviceKey, item.deviceName)}
                                <span style={{ fontSize: '11px', color: '#374151', fontWeight: '600' }}>{item.deviceName}</span>
                              </div>
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#374151', borderBottom: borderStyle }}>
                              {formatInteger(item.clicks)}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#374151', borderBottom: borderStyle }}>
                              {item.clicks > 0 ? ((item.conversions / item.clicks) * 100).toFixed(1) + '%' : '-'}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', fontWeight: '600', borderBottom: borderStyle }}>
                              {formatNumber(item.conversions, 1)}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#374151', borderBottom: borderStyle }}>
                              {item.cpc > 0 ? formatCPC(item.cpc) : '-'}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#374151', borderBottom: borderStyle }}>
                              {formatCurrency(item.cost)}
                            </td>
                            <td style={{
                              padding: '10px 8px',
                              textAlign: 'right',
                              fontSize: '13px',
                              color: (() => {
                                const targetCpa = customObjectives[campaign.campaignId]?.objectiveType === 'CPA'
                                  ? customObjectives[campaign.campaignId].objectiveValue
                                  : campaign.targetCpa;
                                if (!targetCpa || item.cpa <= 0) return '#374151';
                                return item.cpa > targetCpa ? '#dc2626' : item.cpa < targetCpa ? '#16a34a' : '#374151';
                              })(),
                              fontWeight: (() => {
                                const targetCpa = customObjectives[campaign.campaignId]?.objectiveType === 'CPA'
                                  ? customObjectives[campaign.campaignId].objectiveValue
                                  : campaign.targetCpa;
                                if (!targetCpa || item.cpa <= 0) return 'normal';
                                return item.cpa !== targetCpa ? '600' : 'normal';
                              })(),
                              borderBottom: borderStyle
                            }}>
                              {item.cpa > 0 ? formatCurrency(item.cpa) : '-'}
                            </td>
                            <td style={{
                              padding: '10px 8px',
                              textAlign: 'right',
                              fontSize: '13px',
                              color: (() => {
                                const targetRoas = customObjectives[campaign.campaignId]?.objectiveType === 'ROAS'
                                  ? customObjectives[campaign.campaignId].objectiveValue / 100
                                  : campaign.targetRoas;
                                if (!targetRoas || item.roas <= 0) return '#374151';
                                return item.roas < targetRoas ? '#dc2626' : item.roas > targetRoas ? '#16a34a' : '#374151';
                              })(),
                              fontWeight: (() => {
                                const targetRoas = customObjectives[campaign.campaignId]?.objectiveType === 'ROAS'
                                  ? customObjectives[campaign.campaignId].objectiveValue / 100
                                  : campaign.targetRoas;
                                if (!targetRoas || item.roas <= 0) return 'normal';
                                return item.roas !== targetRoas ? '600' : 'normal';
                              })(),
                              borderBottom: borderStyle
                            }}>
                              {formatPercent(item.roas)}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'center', fontSize: '12px', borderBottom: borderStyle }}>
                              {item.currentAdjustment !== 0 ? (
                                <span style={{
                                  color: item.currentAdjustment > 0 ? '#16a34a' : '#dc2626',
                                  fontWeight: '600'
                                }}>
                                  {item.currentAdjustment > 0 ? '+' : ''}{Math.round(item.currentAdjustment * 100)}%
                                </span>
                              ) : (
                                <span style={{ color: '#9ca3af' }}>0%</span>
                              )}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'center', fontSize: '11px', color: '#6b7280', borderBottom: borderStyle }}>
                              {item.lastModified ? (
                                <span title={new Date(item.lastModified).toLocaleString('fr-FR')}>
                                  {Math.floor((new Date() - new Date(item.lastModified)) / (1000 * 60 * 60 * 24))} jours
                                </span>
                              ) : (
                                <span style={{ color: '#d1d5db' }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '10px 12px', borderBottom: borderStyle }}>
                              {getRecommendationBadge(item, campaign.campaignName, 'campaign')}
                              {item.constraintWarning && item.campaignType !== 'PERFORMANCE_MAX' && item.biddingStrategyType !== 'TARGET_ROAS' && item.biddingStrategyType !== 'MAXIMIZE_CONVERSION_VALUE' && item.biddingStrategyType !== 'TARGET_IMPRESSION_SHARE' && (
                                <div style={{ fontSize: '10px', color: '#92400e', marginTop: '4px', maxWidth: '150px' }}>
                                  {item.constraintWarning}
                                </div>
                              )}
                              {item.reason && !item.constraintWarning && item.campaignType !== 'PERFORMANCE_MAX' && item.biddingStrategyType !== 'TARGET_ROAS' && item.biddingStrategyType !== 'MAXIMIZE_CONVERSION_VALUE' && item.biddingStrategyType !== 'TARGET_IMPRESSION_SHARE' && (
                                <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px', maxWidth: '150px' }}>
                                  {item.reason}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'center', borderBottom: borderStyle }}>
                              {getConfidenceBadge(item)}
                            </td>
                          </tr>
                        );
                      })}
                      {/* Ligne Total Campagne - seulement si pas de filtre actif */}
                      {!hasActiveFilter && (
                        <tr style={{ backgroundColor: 'white', fontWeight: '600' }}>
                          <td style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '16px solid white' }}>
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: '600' }}>Total</span>
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', fontWeight: '600', borderBottom: '16px solid white' }}>
                            {formatInteger(campaignTotalClicks)}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', fontWeight: '600', borderBottom: '16px solid white' }}>
                            {campaignTotalClicks > 0 ? ((campaignTotalConversions / campaignTotalClicks) * 100).toFixed(1) + '%' : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', fontWeight: '600', borderBottom: '16px solid white' }}>
                            {formatNumber(campaignTotalConversions)}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', fontWeight: '600', borderBottom: '16px solid white' }}>
                            {campaignTotalClicks > 0 ? formatCPC(campaignTotalCost / campaignTotalClicks) : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', fontWeight: '600', borderBottom: '16px solid white' }}>
                            {formatCurrency(campaignTotalCost)}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', fontWeight: '600', borderBottom: '16px solid white' }}>
                            {campaignAvgCpa > 0 ? formatCurrency(campaignAvgCpa) : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', fontWeight: '600', borderBottom: '16px solid white' }}>
                            {formatPercent(campaignAvgRoas)}
                          </td>
                          <td colSpan={4} style={{ padding: '10px 8px', borderBottom: '16px solid white' }}></td>
                        </tr>
                      )}

                      {/* Adgroups (si expandé) - seulement pour les campagnes non-PMAX */}
                      {isExpanded && campaign.campaignType !== 'PERFORMANCE_MAX' && campaign.adgroups.map((adgroup, adgroupIndex) => {
                        const isLastAdgroup = adgroupIndex === campaign.adgroups.length - 1;
                        // Filtrer les devices de l'adgroup selon les filtres actifs
                        const adgroupDevices = Object.entries(adgroup.devices)
                          .filter(([deviceKey, rec]) => {
                            // Filtre par appareil
                            if (deviceFilter && deviceKey !== deviceFilter) return false;

                            // Filtre par type de recommandation
                            if (recommendationFilter) {
                              let recType = 'maintain';
                              if (rec.status === 'INSUFFICIENT_DATA') recType = 'insufficient_data';
                              else if (rec.status === 'NOT_SUPPORTED' || rec.status === 'INFORMATIONAL') recType = 'not_applicable';
                              else if (rec.finalAdjustmentPercent === -100) recType = 'exclusion';
                              else if (rec.finalAdjustmentPercent > 5) recType = 'increase';
                              else if (rec.finalAdjustmentPercent < -5) recType = 'decrease';

                              if (recommendationFilter === 'actionable') {
                                if (!['increase', 'decrease', 'exclusion'].includes(recType)) return false;
                              } else if (recType !== recommendationFilter) {
                                return false;
                              }
                            }
                            return true;
                          })
                          .sort(([a], [b]) => {
                            const orderA = DEVICE_ORDER.indexOf(a);
                            const orderB = DEVICE_ORDER.indexOf(b);
                            return (orderA === -1 ? 999 : orderA) - (orderB === -1 ? 999 : orderB);
                          });

                        // Si aucun device ne correspond aux filtres, ne pas afficher l'adgroup
                        if (adgroupDevices.length === 0) return null;

                        // Calculer totaux adgroup (sur les devices filtrés)
                        let adgroupTotalCost = 0, adgroupTotalConversions = 0, adgroupTotalConversionsValue = 0, adgroupTotalClicks = 0;
                        adgroupDevices.forEach(([, rec]) => {
                          adgroupTotalCost += rec.displayCost || 0;
                          adgroupTotalConversions += rec.displayConversions || 0;
                          adgroupTotalConversionsValue += rec.displayConversionsValue || 0;
                          adgroupTotalClicks += rec.displayClicks || 0;
                        });
                        const adgroupAvgCpa = adgroupTotalConversions > 0 ? adgroupTotalCost / adgroupTotalConversions : 0;
                        const adgroupAvgRoas = adgroupTotalCost > 0 ? adgroupTotalConversionsValue / adgroupTotalCost : 0;

                        const adgroupDeviceRowCount = hasActiveFilter ? adgroupDevices.length : adgroupDevices.length + 1;

                        return (
                          <React.Fragment key={`adgroup-${adgroup.adgroupId}`}>
                            {adgroupDevices.map(([deviceKey, rec], deviceIndex) => {
                              const isFirst = deviceIndex === 0;
                              const isLastDevice = deviceIndex === adgroupDevices.length - 1;
                              const isLastDeviceOfLastAdgroup = isLastAdgroup && isLastDevice;
                              // Bordure de séparation : 16px blanc pour le dernier device du dernier adgroup
                              const borderStyle = isLastDeviceOfLastAdgroup ? '16px solid white' : 'none';

                              // Récupérer le statut outlier pour ce device
                              const outlierInfo = adgroup.outlierStatusByDevice?.[deviceKey];
                              const isUnderperformer = outlierInfo?.status === 'UNDERPERFORMER';
                              const isOverperformer = outlierInfo?.status === 'OVERPERFORMER';

                              // Type de recommandation
                              let recommendationType = 'maintain';
                              if (rec.status === 'INSUFFICIENT_DATA') recommendationType = 'insufficient_data';
                              else if (rec.status === 'NOT_SUPPORTED' || rec.status === 'INFORMATIONAL') recommendationType = 'not_applicable';
                              else if (rec.finalAdjustmentPercent === -100) recommendationType = 'exclusion';
                              else if (rec.finalAdjustmentPercent > 5) recommendationType = 'increase';
                              else if (rec.finalAdjustmentPercent < -5) recommendationType = 'decrease';

                              // Couleur de fond : outliers plus visibles
                              let rowBgColor = '#fafafa';
                              if (isUnderperformer) {
                                rowBgColor = '#fef2f2'; // Rouge clair pour sous-performeurs
                              } else if (isOverperformer) {
                                rowBgColor = '#f0fdf4'; // Vert clair pour sur-performeurs
                              } else if (recommendationType === 'increase') {
                                rowBgColor = '#f0fdf4';
                              } else if (['decrease', 'exclusion'].includes(recommendationType)) {
                                rowBgColor = '#fef2f2';
                              }

                              // Couleur de fond pour la cellule nom (outliers)
                              let nameCellBgColor = '#fefce8'; // Jaune par défaut
                              if (isUnderperformer) {
                                nameCellBgColor = '#fee2e2'; // Rouge pour sous-performeurs
                              } else if (isOverperformer) {
                                nameCellBgColor = '#dcfce7'; // Vert pour sur-performeurs
                              }

                              const item = {
                                ...rec,
                                device: deviceKey,
                                deviceName: DEVICE_NAMES[deviceKey] || deviceKey,
                                recommendationType,
                                adjustmentPercent: rec.rawAdjustmentPercent,
                                finalAdjustmentPercent: rec.finalAdjustmentPercent,
                                canApply: rec.canApply !== false,
                                constraintWarning: rec.constraintWarning,
                                reason: rec.reason,
                                clicks: rec.displayClicks || 0,
                                conversions: rec.displayConversions || 0,
                                cost: rec.displayCost || 0,
                                cpa: rec.displayCPA || 0,
                                roas: rec.displayROAS || 0,
                                cpc: rec.displayCPC || 0,
                                currentAdjustment: rec.currentAdjustment || 0,
                                isInheritedAdjustment: rec.isInheritedAdjustment || false,
                                lastModified: rec.lastModified || null,
                                lookbackWindow: rec.lookbackWindow,
                                confidenceLevel: rec.confidenceLevel,
                                confidenceScore: rec.confidenceScore,
                                campaignType: campaign.campaignType
                              };

                              // Vérifier si l'adgroup a au moins un outlier (pour le badge)
                              const hasAnyOutlier = Object.keys(adgroup.outlierStatusByDevice || {}).length > 0;
                              const firstOutlier = Object.values(adgroup.outlierStatusByDevice || {})[0];

                              return (
                                <tr key={`adgroup-${adgroup.adgroupId}-${deviceKey}`} style={{ backgroundColor: rowBgColor }}>
                                  {isFirst && (
                                    <td rowSpan={adgroupDeviceRowCount} style={{ padding: '12px', paddingLeft: '40px', borderBottom: isLastAdgroup ? '16px solid white' : '1px solid #f3f4f6', verticalAlign: 'top', backgroundColor: nameCellBgColor }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                          <span style={{ fontSize: '12px', fontWeight: '500', color: hasAnyOutlier ? (firstOutlier?.status === 'UNDERPERFORMER' ? '#991b1b' : '#166534') : '#92400e' }}>
                                            {adgroup.adgroupName}
                                          </span>
                                          {hasAnyOutlier && (
                                            <span style={{
                                              fontSize: '9px',
                                              padding: '1px 5px',
                                              borderRadius: '3px',
                                              backgroundColor: firstOutlier?.status === 'UNDERPERFORMER' ? '#991b1b' : '#166534',
                                              color: 'white',
                                              fontWeight: '600',
                                              textTransform: 'uppercase'
                                            }}>
                                              {firstOutlier?.status === 'UNDERPERFORMER' ? 'Sous-perf.' : 'Sur-perf.'}
                                            </span>
                                          )}
                                        </div>
                                        <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                                          {adgroupAvgCpa > 0 && <span>CPA : {formatCurrency(adgroupAvgCpa)}</span>}
                                          {adgroupAvgCpa > 0 && adgroupAvgRoas > 0 && <span> • </span>}
                                          {adgroupAvgRoas > 0 && <span>ROAS : {(adgroupAvgRoas * 100).toFixed(0)}%</span>}
                                        </div>
                                        {/* Objectif personnalisé hérité de la campagne */}
                                        {customObjectives[campaign.campaignId] && (
                                          <div style={{ fontSize: '10px', marginTop: '4px' }}>
                                            <span style={{
                                              backgroundColor: '#dbeafe',
                                              color: '#1e40af',
                                              padding: '2px 6px',
                                              borderRadius: '8px',
                                              fontWeight: '600'
                                            }}>
                                              🎯 {customObjectives[campaign.campaignId].objectiveType === 'CPA'
                                                ? `CPA ${formatCurrency(customObjectives[campaign.campaignId].objectiveValue)}`
                                                : `ROAS ${customObjectives[campaign.campaignId].objectiveValue.toFixed(0)}%`
                                              }
                                            </span>
                                          </div>
                                        )}
                                        {hasAnyOutlier && firstOutlier?.deviationPercent !== undefined && (
                                          <div style={{ fontSize: '9px', color: firstOutlier?.status === 'UNDERPERFORMER' ? '#991b1b' : '#166534', marginTop: '2px' }}>
                                            Écart: {firstOutlier.deviationPercent > 0 ? '+' : ''}{firstOutlier.deviationPercent}% vs moyenne
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  )}
                                  <td style={{ padding: '10px 8px', textAlign: 'center', borderBottom: borderStyle }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                                      {getDeviceIcon(deviceKey, item.deviceName)}
                                      <span style={{ fontSize: '11px', color: '#374151', fontWeight: '600' }}>{item.deviceName}</span>
                                    </div>
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#374151', borderBottom: borderStyle }}>
                                    {formatInteger(item.clicks)}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#374151', borderBottom: borderStyle }}>
                                    {item.clicks > 0 ? ((item.conversions / item.clicks) * 100).toFixed(1) + '%' : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', fontWeight: '600', borderBottom: borderStyle }}>
                                    {formatNumber(item.conversions, 1)}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#374151', borderBottom: borderStyle }}>
                                    {item.cpc > 0 ? formatCPC(item.cpc) : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#374151', borderBottom: borderStyle }}>
                                    {formatCurrency(item.cost)}
                                  </td>
                                  <td style={{
                                    padding: '10px 8px',
                                    textAlign: 'right',
                                    fontSize: '13px',
                                    color: (() => {
                                      const targetCpa = customObjectives[campaign.campaignId]?.objectiveType === 'CPA'
                                        ? customObjectives[campaign.campaignId].objectiveValue
                                        : campaign.targetCpa;
                                      if (!targetCpa || item.cpa <= 0) return '#374151';
                                      return item.cpa > targetCpa ? '#dc2626' : item.cpa < targetCpa ? '#16a34a' : '#374151';
                                    })(),
                                    fontWeight: (() => {
                                      const targetCpa = customObjectives[campaign.campaignId]?.objectiveType === 'CPA'
                                        ? customObjectives[campaign.campaignId].objectiveValue
                                        : campaign.targetCpa;
                                      if (!targetCpa || item.cpa <= 0) return 'normal';
                                      return item.cpa !== targetCpa ? '600' : 'normal';
                                    })(),
                                    borderBottom: borderStyle
                                  }}>
                                    {item.cpa > 0 ? formatCurrency(item.cpa) : '-'}
                                  </td>
                                  <td style={{
                                    padding: '10px 8px',
                                    textAlign: 'right',
                                    fontSize: '13px',
                                    color: (() => {
                                      const targetRoas = customObjectives[campaign.campaignId]?.objectiveType === 'ROAS'
                                        ? customObjectives[campaign.campaignId].objectiveValue / 100
                                        : campaign.targetRoas;
                                      if (!targetRoas || item.roas <= 0) return '#374151';
                                      return item.roas < targetRoas ? '#dc2626' : item.roas > targetRoas ? '#16a34a' : '#374151';
                                    })(),
                                    fontWeight: (() => {
                                      const targetRoas = customObjectives[campaign.campaignId]?.objectiveType === 'ROAS'
                                        ? customObjectives[campaign.campaignId].objectiveValue / 100
                                        : campaign.targetRoas;
                                      if (!targetRoas || item.roas <= 0) return 'normal';
                                      return item.roas !== targetRoas ? '600' : 'normal';
                                    })(),
                                    borderBottom: borderStyle
                                  }}>
                                    {formatPercent(item.roas)}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'center', fontSize: '12px', borderBottom: borderStyle }}>
                                    {item.currentAdjustment !== 0 ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                        <span style={{
                                          color: item.currentAdjustment > 0 ? '#16a34a' : '#dc2626',
                                          fontWeight: '600'
                                        }}>
                                          {item.currentAdjustment > 0 ? '+' : ''}{Math.round(item.currentAdjustment * 100)}%
                                        </span>
                                        {item.isInheritedAdjustment && (
                                          <span style={{ fontSize: '9px', color: '#9ca3af', fontStyle: 'italic' }}>hérité</span>
                                        )}
                                      </div>
                                    ) : (
                                      <span style={{ color: '#9ca3af' }}>0%</span>
                                    )}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'center', fontSize: '11px', color: '#6b7280', borderBottom: borderStyle }}>
                                    {item.lastModified ? (
                                      <span title={new Date(item.lastModified).toLocaleString('fr-FR')}>
                                        {Math.floor((new Date() - new Date(item.lastModified)) / (1000 * 60 * 60 * 24))} jours
                                      </span>
                                    ) : (
                                      <span style={{ color: '#d1d5db' }}>—</span>
                                    )}
                                  </td>
                                  <td style={{ padding: '10px 12px', borderBottom: borderStyle }}>
                                    {getRecommendationBadge(item, adgroup.adgroupName, 'adgroup')}
                                    {item.constraintWarning && item.campaignType !== 'PERFORMANCE_MAX' && item.biddingStrategyType !== 'TARGET_ROAS' && item.biddingStrategyType !== 'MAXIMIZE_CONVERSION_VALUE' && item.biddingStrategyType !== 'TARGET_IMPRESSION_SHARE' && (
                                      <div style={{ fontSize: '10px', color: '#92400e', marginTop: '4px', maxWidth: '150px' }}>
                                        {item.constraintWarning}
                                      </div>
                                    )}
                                    {item.reason && !item.constraintWarning && item.campaignType !== 'PERFORMANCE_MAX' && item.biddingStrategyType !== 'TARGET_ROAS' && item.biddingStrategyType !== 'MAXIMIZE_CONVERSION_VALUE' && item.biddingStrategyType !== 'TARGET_IMPRESSION_SHARE' && (
                                      <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px', maxWidth: '150px' }}>
                                        {item.reason}
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'center', borderBottom: borderStyle }}>
                                    {getConfidenceBadge(item)}
                                  </td>
                                </tr>
                              );
                            })}
                            {/* Ligne Total Adgroup - seulement si pas de filtre actif */}
                            {!hasActiveFilter && (
                              <tr style={{ backgroundColor: 'white', fontWeight: '600' }}>
                                <td style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                                  <span style={{ fontSize: '11px', color: '#374151', fontWeight: '600' }}>Total</span>
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#111827', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>
                                  {formatInteger(adgroupTotalClicks)}
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#111827', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>
                                  {adgroupTotalClicks > 0 ? ((adgroupTotalConversions / adgroupTotalClicks) * 100).toFixed(1) + '%' : '-'}
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#111827', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>
                                  {formatNumber(adgroupTotalConversions)}
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#111827', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>
                                  {adgroupTotalClicks > 0 ? formatCurrency(adgroupTotalCost / adgroupTotalClicks) : '-'}
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#111827', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>
                                  {formatCurrency(adgroupTotalCost)}
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#111827', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>
                                  {adgroupAvgCpa > 0 ? formatCurrency(adgroupAvgCpa) : '-'}
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#111827', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>
                                  {formatPercent(adgroupAvgRoas)}
                                </td>
                                <td colSpan={4} style={{ padding: '10px 8px', borderBottom: '1px solid #e5e7eb' }}>
                                  <span style={{ color: '#9ca3af' }}>—</span>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}

                      {/* Asset Groups pour campagnes PMAX (si expandé) */}
                      {isExpanded && campaign.campaignType === 'PERFORMANCE_MAX' && (
                        <>
                          {/* Chargement en cours */}
                          {loadingAssetGroups.has(campaign.campaignId) && (
                            <tr style={{ backgroundColor: '#f3e8ff' }}>
                              <td colSpan={15} style={{ padding: '12px 12px 12px 40px', fontSize: '13px', color: '#7c3aed' }}>
                                Chargement des groupes de composants...
                              </td>
                            </tr>
                          )}

                          {/* Aucun asset group trouvé */}
                          {!loadingAssetGroups.has(campaign.campaignId) && assetGroupsData[campaign.campaignId] && assetGroupsData[campaign.campaignId].length === 0 && (
                            <tr style={{ backgroundColor: '#fef2f2' }}>
                              <td colSpan={15} style={{ padding: '12px 12px 12px 40px', fontSize: '13px', color: '#991b1b' }}>
                                Aucun groupe de composants trouvé pour cette campagne
                              </td>
                            </tr>
                          )}

                          {/* Liste des asset groups */}
                          {assetGroupsData[campaign.campaignId] && assetGroupsData[campaign.campaignId].map((assetGroup, assetGroupIndex) => {
                            const isLast = assetGroupIndex === assetGroupsData[campaign.campaignId].length - 1;
                            const assetGroupRoas = assetGroup.cost > 0 ? assetGroup.conversionsValue / assetGroup.cost : 0;
                            const assetGroupCpa = assetGroup.conversions > 0 ? assetGroup.cost / assetGroup.conversions : 0;
                            const assetGroupCtr = assetGroup.impressions > 0 ? (assetGroup.clicks / assetGroup.impressions) * 100 : 0;
                            const assetGroupCpc = assetGroup.clicks > 0 ? assetGroup.cost / assetGroup.clicks : 0;
                            const assetGroupConvRate = assetGroup.clicks > 0 ? (assetGroup.conversions / assetGroup.clicks) * 100 : 0;

                            return (
                              <tr key={`asset-group-${assetGroup.name}-${assetGroupIndex}`} style={{ backgroundColor: '#faf5ff' }}>
                                <td style={{
                                  padding: '12px',
                                  paddingLeft: '40px',
                                  borderBottom: isLast ? '16px solid white' : '1px solid #f3e8ff',
                                  verticalAlign: 'top',
                                  backgroundColor: '#f3e8ff'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                    <Layers style={{ width: '14px', height: '14px', color: '#7c3aed', flexShrink: 0, marginTop: '2px' }} />
                                    <div>
                                      <span style={{ fontSize: '13px', fontWeight: '500', color: '#5b21b6' }}>
                                        {assetGroup.name}
                                      </span>
                                      <div style={{ fontSize: '11px', color: '#7c3aed', marginTop: '2px' }}>
                                        {assetGroupCpa > 0 && <span>CPA : {formatCurrency(assetGroupCpa)}</span>}
                                        {assetGroupCpa > 0 && assetGroupRoas > 0 && <span> • </span>}
                                        {assetGroupRoas > 0 && <span>ROAS : {(assetGroupRoas * 100).toFixed(0)}%</span>}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'center', borderBottom: isLast ? '16px solid white' : '1px solid #f3e8ff' }}>
                                  <span style={{ fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>—</span>
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#374151', borderBottom: isLast ? '16px solid white' : '1px solid #f3e8ff' }}>
                                  {formatInteger(assetGroup.impressions || 0)}
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#374151', borderBottom: isLast ? '16px solid white' : '1px solid #f3e8ff' }}>
                                  {formatInteger(assetGroup.clicks || 0)}
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#374151', borderBottom: isLast ? '16px solid white' : '1px solid #f3e8ff' }}>
                                  {assetGroupCtr.toFixed(1)}%
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#374151', borderBottom: isLast ? '16px solid white' : '1px solid #f3e8ff' }}>
                                  {assetGroupConvRate.toFixed(1)}%
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#374151', borderBottom: isLast ? '16px solid white' : '1px solid #f3e8ff' }}>
                                  {formatNumber(assetGroup.conversions || 0, 1)}
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#374151', borderBottom: isLast ? '16px solid white' : '1px solid #f3e8ff' }}>
                                  {formatCurrency(assetGroupCpc)}
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#374151', borderBottom: isLast ? '16px solid white' : '1px solid #f3e8ff' }}>
                                  {formatCurrency(assetGroup.cost || 0)}
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#374151', borderBottom: isLast ? '16px solid white' : '1px solid #f3e8ff' }}>
                                  {assetGroupCpa > 0 ? formatCurrency(assetGroupCpa) : '-'}
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', color: '#374151', borderBottom: isLast ? '16px solid white' : '1px solid #f3e8ff' }}>
                                  {formatPercent(assetGroupRoas)}
                                </td>
                                <td colSpan={4} style={{ padding: '10px 8px', borderBottom: isLast ? '16px solid white' : '1px solid #f3e8ff' }}>
                                  <span style={{ fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>
                                    Ajustements d'enchères indisponibles
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Vue non-hiérarchique (campagnes ou adgroups seuls) */}
                {settings.level !== 'hierarchical' && filteredData.filter(item => !item.isTotal).map((item, index, filteredItems) => {
                  // Déterminer si c'est la première ligne d'un groupe (entité)
                  const prevItem = index > 0 ? filteredItems[index - 1] : null;
                  const isFirstOfGroup = !prevItem || prevItem.entityId !== item.entityId;

                  // Compter le nombre d'appareils dans ce groupe pour le rowspan
                  let groupSize = 1;
                  if (isFirstOfGroup) {
                    for (let i = index + 1; i < filteredItems.length; i++) {
                      const nextItem = filteredItems[i];
                      if (nextItem.entityId !== item.entityId) {
                        break;
                      }
                      groupSize++;
                    }
                    // +1 pour la ligne total seulement si pas de filtre actif
                    if (!recommendationFilter && !deviceFilter) {
                      groupSize++;
                    }
                  }

                  // Déterminer si c'est la dernière ligne avant le total
                  const nextItem = index < filteredItems.length - 1 ? filteredItems[index + 1] : null;
                  const isLastBeforeTotal = !nextItem || nextItem.entityId !== item.entityId;

                  // Trouver la ligne total correspondante
                  const totalItem = isLastBeforeTotal ? filteredData.find(t =>
                    t.isTotal && t.entityId === item.entityId
                  ) : null;

                  // Bordure épaisse pour la dernière ligne du groupe
                  const hasActiveFilter = recommendationFilter || deviceFilter;
                  const isLastRowOfGroup = isLastBeforeTotal && hasActiveFilter;
                  const borderStyle = isLastRowOfGroup ? '2px solid #e5e7eb' : '1px solid #f3f4f6';
                  const totalBorderStyle = '2px solid #e5e7eb';

                  // Couleur de fond selon la recommandation
                  const rowBgColor = ['increase'].includes(item.recommendationType) ? '#dcfce7'
                    : ['decrease', 'exclusion'].includes(item.recommendationType) ? '#fee2e2'
                    : 'white';

                  return (
                    <React.Fragment key={`${item.entityId}-${item.device}-${index}`}>
                      <tr style={{ backgroundColor: rowBgColor }}>
                        {isFirstOfGroup && (
                          <td rowSpan={groupSize} style={{ padding: '12px', borderBottom: '16px solid white', verticalAlign: 'top', backgroundColor: '#f9fafb' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {item.entityType === 'adgroup' ? (
                                <>
                                  <span style={{ fontSize: '13px', fontWeight: '500', color: '#111827' }}>{item.adGroupName}</span>
                                  <span style={{ fontSize: '11px', color: '#6b7280' }}>{item.campaignName}</span>
                                </>
                              ) : (
                                <span style={{ fontSize: '13px', fontWeight: '500', color: '#111827' }}>{item.campaignName}</span>
                              )}
                              <div style={{ fontSize: '11px', color: '#374151', marginTop: '4px' }}>
                                <span style={{ color: '#6b7280' }}>Stratégie :</span> <span style={{ fontWeight: '500' }}>{BIDDING_STRATEGY_LABELS[item.biddingStrategyType] || item.biddingStrategyType}</span>
                              </div>
                              {(item.entityAvgCpa > 0 || item.entityAvgRoas > 0 || item.targetCpa || item.targetRoas || customObjectives[item.entityId]) && (
                                <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  {customObjectives[item.entityId] ? (
                                    <span style={{
                                      backgroundColor: '#dbeafe',
                                      color: '#1e40af',
                                      padding: '3px 8px',
                                      borderRadius: '10px',
                                      fontWeight: '600',
                                      fontSize: '11px'
                                    }}>
                                      🎯 Objectif : {customObjectives[item.entityId].objectiveType === 'CPA'
                                        ? `CPA ${formatCurrency(customObjectives[item.entityId].objectiveValue)}`
                                        : `ROAS ${customObjectives[item.entityId].objectiveValue.toFixed(0)}%`
                                      }
                                    </span>
                                  ) : item.biddingStrategyType === 'MAXIMIZE_CONVERSION_VALUE' ? (
                                    // Maximiser la valeur de conversion → ROAS uniquement
                                    item.entityAvgRoas > 0 && (
                                      <span style={{
                                        backgroundColor: '#f3f4f6',
                                        color: '#4b5563',
                                        padding: '3px 8px',
                                        borderRadius: '10px',
                                        fontWeight: '600',
                                        fontSize: '11px'
                                      }}>
                                        🎯 Objectif : ROAS {(item.entityAvgRoas * 100).toFixed(0)}% moyen
                                      </span>
                                    )
                                  ) : item.biddingStrategyType === 'MAXIMIZE_CONVERSIONS' ? (
                                    // Maximiser les conversions → CPA uniquement
                                    item.entityAvgCpa > 0 && (
                                      <span style={{
                                        backgroundColor: '#f3f4f6',
                                        color: '#4b5563',
                                        padding: '3px 8px',
                                        borderRadius: '10px',
                                        fontWeight: '600',
                                        fontSize: '11px'
                                      }}>
                                        🎯 Objectif : CPA {formatCurrency(item.entityAvgCpa)} moyen
                                      </span>
                                    )
                                  ) : ['MANUAL_CPC', 'MANUAL_CPM', 'MAXIMIZE_CLICKS'].includes(item.biddingStrategyType) ? (
                                    // Stratégies manuelles → CPA et ROAS
                                    (item.entityAvgCpa > 0 || item.entityAvgRoas > 0) && (
                                      <span style={{
                                        backgroundColor: '#f3f4f6',
                                        color: '#4b5563',
                                        padding: '3px 8px',
                                        borderRadius: '10px',
                                        fontWeight: '600',
                                        fontSize: '11px'
                                      }}>
                                        🎯 Objectif : {item.entityAvgCpa > 0 && `CPA ${formatCurrency(item.entityAvgCpa)}`}{item.entityAvgCpa > 0 && item.entityAvgRoas > 0 && ' et '}{item.entityAvgRoas > 0 && `ROAS ${(item.entityAvgRoas * 100).toFixed(0)}%`} moyens
                                      </span>
                                    )
                                  ) : (
                                    (item.targetCpa || item.targetRoas) && (
                                      <span style={{
                                        backgroundColor: '#f3f4f6',
                                        color: '#4b5563',
                                        padding: '3px 8px',
                                        borderRadius: '10px',
                                        fontWeight: '600',
                                        fontSize: '11px'
                                      }}>
                                        🎯 Objectif : {item.targetCpa && `CPA ${formatCurrency(item.targetCpa)}`}{item.targetCpa && item.targetRoas && ' et '}{item.targetRoas && `ROAS ${(item.targetRoas * 100).toFixed(0)}%`}
                                      </span>
                                    )
                                  )}
                                  {item.entityType === 'campaign' && (
                                    <>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openObjectiveEditor(item.entityId, item.campaignName, item.targetCpa, item.targetRoas);
                                        }}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          padding: '2px',
                                          cursor: 'pointer',
                                          color: '#6b7280',
                                          display: 'inline-flex',
                                          alignItems: 'center'
                                        }}
                                        title="Modifier l'objectif"
                                      >
                                        <Edit3 style={{ width: '12px', height: '12px' }} />
                                      </button>
                                      {customObjectives[item.entityId] && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            deleteCustomObjective(item.entityId);
                                          }}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: '2px',
                                            cursor: 'pointer',
                                            color: '#991b1b',
                                            display: 'inline-flex',
                                            alignItems: 'center'
                                          }}
                                          title="Supprimer l'objectif personnalisé"
                                        >
                                          <X style={{ width: '12px', height: '12px' }} />
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                              {/* Bouton pour ajouter un objectif si aucun n'est défini */}
                              {item.entityType === 'campaign' && !item.entityAvgCpa && !item.entityAvgRoas && !item.targetCpa && !item.targetRoas && !customObjectives[item.entityId] && (
                                <div style={{ fontSize: '10px', marginTop: '2px' }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openObjectiveEditor(item.entityId, item.campaignName, null, null);
                                    }}
                                    style={{
                                      background: 'none',
                                      border: '1px dashed #d1d5db',
                                      borderRadius: '4px',
                                      padding: '2px 8px',
                                      cursor: 'pointer',
                                      color: '#6b7280',
                                      fontSize: '10px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                    title="Définir un objectif personnalisé"
                                  >
                                    <Edit3 style={{ width: '10px', height: '10px' }} />
                                    Définir un objectif
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                        <td style={{ padding: '10px 8px', textAlign: 'center', borderBottom: borderStyle }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                            {getDeviceIcon(item.device, item.deviceName)}
                            <span style={{ fontSize: '11px', color: '#374151', fontWeight: '600' }}>{item.deviceName}</span>
                          </div>
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#374151', borderBottom: borderStyle }}>
                          {formatInteger(item.clicks)}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#374151', borderBottom: borderStyle }}>
                          {item.clicks > 0 ? ((item.conversions / item.clicks) * 100).toFixed(1) + '%' : '-'}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', fontWeight: '600', borderBottom: borderStyle }}>
                          {formatNumber(item.conversions, 1)}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#374151', borderBottom: borderStyle }}>
                          {item.cpc > 0 ? formatCPC(item.cpc) : '-'}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#374151', borderBottom: borderStyle }}>
                          {formatCurrency(item.cost)}
                        </td>
                        <td style={{
                          padding: '10px 8px',
                          textAlign: 'right',
                          fontSize: '13px',
                          color: (() => {
                            const targetCpa = customObjectives[item.campaignId]?.objectiveType === 'CPA'
                              ? customObjectives[item.campaignId].objectiveValue
                              : item.targetCpa;
                            if (!targetCpa || item.cpa <= 0) return '#374151';
                            return item.cpa > targetCpa ? '#dc2626' : item.cpa < targetCpa ? '#16a34a' : '#374151';
                          })(),
                          fontWeight: (() => {
                            const targetCpa = customObjectives[item.campaignId]?.objectiveType === 'CPA'
                              ? customObjectives[item.campaignId].objectiveValue
                              : item.targetCpa;
                            if (!targetCpa || item.cpa <= 0) return 'normal';
                            return item.cpa !== targetCpa ? '600' : 'normal';
                          })(),
                          borderBottom: borderStyle
                        }}>
                          {item.cpa > 0 ? formatCurrency(item.cpa) : '-'}
                        </td>
                        <td style={{
                          padding: '10px 8px',
                          textAlign: 'right',
                          fontSize: '13px',
                          color: (() => {
                            const targetRoas = customObjectives[item.campaignId]?.objectiveType === 'ROAS'
                              ? customObjectives[item.campaignId].objectiveValue / 100
                              : item.targetRoas;
                            if (!targetRoas || item.roas <= 0) return '#374151';
                            return item.roas < targetRoas ? '#dc2626' : item.roas > targetRoas ? '#16a34a' : '#374151';
                          })(),
                          fontWeight: (() => {
                            const targetRoas = customObjectives[item.campaignId]?.objectiveType === 'ROAS'
                              ? customObjectives[item.campaignId].objectiveValue / 100
                              : item.targetRoas;
                            if (!targetRoas || item.roas <= 0) return 'normal';
                            return item.roas !== targetRoas ? '600' : 'normal';
                          })(),
                          borderBottom: borderStyle
                        }}>
                          {formatPercent(item.roas)}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'center', fontSize: '12px', borderBottom: borderStyle }}>
                          {item.currentAdjustment !== 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                              <span style={{
                                color: item.currentAdjustment > 0 ? '#16a34a' : '#dc2626',
                                fontWeight: '600'
                              }}>
                                {item.currentAdjustment > 0 ? '+' : ''}{Math.round(item.currentAdjustment * 100)}%
                              </span>
                              {item.isInheritedAdjustment && (
                                <span style={{ fontSize: '9px', color: '#9ca3af', fontStyle: 'italic' }}>hérité</span>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>0%</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'center', fontSize: '11px', color: '#6b7280', borderBottom: borderStyle }}>
                          {item.lastModified ? (
                            <span title={new Date(item.lastModified).toLocaleString('fr-FR')}>
                              {Math.floor((new Date() - new Date(item.lastModified)) / (1000 * 60 * 60 * 24))} jours
                            </span>
                          ) : (
                            <span style={{ color: '#d1d5db' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px', borderBottom: borderStyle }}>
                          {getRecommendationBadge(item, item.campaignName || item.adgroupName || '', item.entityType || 'campaign')}
                          {item.constraintWarning && item.campaignType !== 'PERFORMANCE_MAX' && item.biddingStrategyType !== 'TARGET_ROAS' && item.biddingStrategyType !== 'MAXIMIZE_CONVERSION_VALUE' && item.biddingStrategyType !== 'TARGET_IMPRESSION_SHARE' && (
                            <div style={{ fontSize: '10px', color: '#92400e', marginTop: '4px', maxWidth: '150px' }}>
                              {item.constraintWarning}
                            </div>
                          )}
                          {item.reason && !item.constraintWarning && item.campaignType !== 'PERFORMANCE_MAX' && item.biddingStrategyType !== 'TARGET_ROAS' && item.biddingStrategyType !== 'MAXIMIZE_CONVERSION_VALUE' && item.biddingStrategyType !== 'TARGET_IMPRESSION_SHARE' && (
                            <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px', maxWidth: '150px' }}>
                              {item.reason}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'center', borderBottom: borderStyle }}>
                          {getConfidenceBadge(item)}
                        </td>
                      </tr>
                      {/* Ligne Total - seulement si pas de filtre actif */}
                      {isLastBeforeTotal && totalItem && !recommendationFilter && !deviceFilter && (
                        <tr style={{ backgroundColor: '#f8fafc', fontWeight: '600' }}>
                          <td style={{ padding: '10px 8px', textAlign: 'center', borderBottom: totalBorderStyle }}>
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: '600' }}>Total</span>
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', borderBottom: totalBorderStyle }}>
                            {formatInteger(totalItem.clicks)}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', borderBottom: totalBorderStyle }}>
                            {totalItem.clicks > 0 ? ((totalItem.conversions / totalItem.clicks) * 100).toFixed(1) + '%' : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', fontWeight: '600', borderBottom: totalBorderStyle }}>
                            {formatNumber(totalItem.conversions, 1)}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', borderBottom: totalBorderStyle }}>
                            {totalItem.clicks > 0 ? formatCurrency(totalItem.cost / totalItem.clicks) : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', borderBottom: totalBorderStyle }}>
                            {formatCurrency(totalItem.cost)}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', borderBottom: totalBorderStyle }}>
                            {totalItem.cpa > 0 ? formatCurrency(totalItem.cpa) : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#111827', borderBottom: totalBorderStyle }}>
                            {formatPercent(totalItem.roas)}
                          </td>
                          <td colSpan={4} style={{ padding: '10px 8px', textAlign: 'center', borderBottom: totalBorderStyle }}>
                            <span style={{ color: '#9ca3af' }}>—</span>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Message si pas de données */}
      {selectedAccount && !loading && recommendations.length === 0 && !error && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '60px 24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <Smartphone style={{ width: '48px', height: '48px', color: '#9ca3af', margin: '0 auto 16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>
            Aucune donnée disponible
          </h3>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: '0' }}>
            Sélectionnez un compte et cliquez sur "Paramètres" pour configurer et récupérer les données
          </p>
        </div>
      )}

      {/* Tooltip enrichi flottant pour la confiance */}
      {tooltipPosition.visible && tooltipPosition.item && (
        <div
          style={{
            position: 'fixed',
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            background: '#1f2937',
            color: 'white',
            padding: '20px 24px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '400',
            textAlign: 'left',
            zIndex: 9999,
            boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
            minWidth: '600px',
            maxWidth: '640px',
            pointerEvents: 'none'
          }}
        >
          <div style={{ marginBottom: '16px' }}>
            <strong style={{ fontSize: '16px' }}>
              Indice de confiance: {((tooltipPosition.item.confidenceScore || 0) * 100).toFixed(0)}%
            </strong>
            <span style={{ marginLeft: '16px', fontSize: '13px', color: '#9ca3af' }}>
              Fenêtre: {tooltipPosition.item.lookbackWindow || '—'}j
            </span>
          </div>

          {/* Graphique */}
          <div style={{ marginBottom: '8px', fontSize: '11px', color: '#9ca3af' }}>
            Historique 90 jours — Conversions (barres) & CPA (courbe)
          </div>
          {renderMiniChart(
            tooltipPosition.item.dailyChartData,
            tooltipPosition.item.referenceValue,
            tooltipPosition.item.referenceMetric
          )}

          {/* Tableau des métriques par fenêtre */}
          <div style={{ marginTop: '14px', marginBottom: '8px', fontSize: '11px', color: '#9ca3af' }}>
            Performance par période
          </div>
          {renderWindowMetricsTable(tooltipPosition.item.windowMetrics)}

          {/* Note explicative */}
          <div style={{ marginTop: '14px', fontSize: '10px', color: '#6b7280', borderTop: '1px solid #374151', paddingTop: '12px' }}>
            Score = Volume conv. pondérées (60%) + Fraîcheur données (40%)
          </div>
        </div>
      )}

      {/* Style pour l'animation de spin et tooltips */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .device-table-container {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .device-table-container::-webkit-scrollbar {
          display: none;
        }
        /* Tooltip pour l'en-tête Confiance */
        .confidence-header .confidence-header-tooltip {
          display: none;
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: #1f2937;
          color: white;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 400;
          text-align: left;
          white-space: nowrap;
          z-index: 100;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          margin-top: 8px;
        }
        .confidence-header .confidence-header-tooltip::before {
          content: '';
          position: absolute;
          top: -6px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid #1f2937;
        }
        .confidence-header:hover .confidence-header-tooltip {
          display: block;
        }
        /* Tooltip pour l'en-tête Recommandation */
        .recommendation-header .recommendation-header-tooltip {
          display: none;
          position: absolute;
          top: 100%;
          right: 0;
          background: #1f2937;
          color: white;
          padding: 14px 18px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 400;
          text-align: left;
          white-space: nowrap;
          z-index: 100;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          margin-top: 8px;
          line-height: 1.5;
        }
        .recommendation-header .recommendation-header-tooltip::before {
          content: '';
          position: absolute;
          top: -6px;
          right: 20px;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid #1f2937;
        }
        .recommendation-header:hover .recommendation-header-tooltip {
          display: block;
        }
        /* Tooltip pour l'en-tête Dernière modif. */
        .lastmodif-header .lastmodif-header-tooltip {
          display: none;
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: #1f2937;
          color: white;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 400;
          text-align: left;
          white-space: nowrap;
          z-index: 100;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          margin-top: 8px;
          line-height: 1.5;
        }
        .lastmodif-header .lastmodif-header-tooltip::before {
          content: '';
          position: absolute;
          top: -6px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid #1f2937;
        }
        .lastmodif-header:hover .lastmodif-header-tooltip {
          display: block;
        }
        /* Tooltip pour les cellules de confiance */
        .confidence-cell .confidence-tooltip {
          display: none;
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: #1f2937;
          color: white;
          padding: 10px 14px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 400;
          text-align: left;
          white-space: nowrap;
          z-index: 100;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          margin-top: 6px;
        }
        .confidence-cell .confidence-tooltip::before {
          content: '';
          position: absolute;
          top: -5px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-bottom: 5px solid #1f2937;
        }
        .confidence-cell:hover .confidence-tooltip {
          display: block;
        }
        /* Masquer les flèches haut/bas des inputs number */
        .adjustment-input::-webkit-inner-spin-button,
        .adjustment-input::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .adjustment-input {
          -moz-appearance: textfield;
        }
      `}</style>

      {/* Modal d'édition de l'objectif */}
      {editingObjective && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}
        onClick={() => setEditingObjective(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '400px',
              maxWidth: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                Modifier l'objectif
              </h3>
              <button
                onClick={() => setEditingObjective(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '4px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                <X style={{ width: '20px', height: '20px' }} />
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#6b7280' }}>
                Campagne : <strong style={{ color: '#111827' }}>{editingObjective.campaignName}</strong>
              </p>
              {(editingObjective.targetCpa || editingObjective.targetRoas) && !customObjectives[editingObjective.campaignId] && (
                <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#9ca3af' }}>
                  Objectif Google Ads actuel :
                  {editingObjective.targetCpa && ` CPA cible ${formatCurrency(editingObjective.targetCpa)}`}
                  {editingObjective.targetRoas && ` ROAS cible ${(editingObjective.targetRoas * 100).toFixed(0)}%`}
                </p>
              )}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                Type d'objectif
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setObjectiveForm(prev => ({ ...prev, type: 'CPA' }))}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: objectiveForm.type === 'CPA' ? '2px solid #333333' : '1px solid #d1d5db',
                    borderRadius: '8px',
                    backgroundColor: objectiveForm.type === 'CPA' ? '#FAF9F5' : 'white',
                    color: objectiveForm.type === 'CPA' ? '#333333' : '#6b7280',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  CPA cible
                </button>
                <button
                  onClick={() => setObjectiveForm(prev => ({ ...prev, type: 'ROAS' }))}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: objectiveForm.type === 'ROAS' ? '2px solid #333333' : '1px solid #d1d5db',
                    borderRadius: '8px',
                    backgroundColor: objectiveForm.type === 'ROAS' ? '#FAF9F5' : 'white',
                    color: objectiveForm.type === 'ROAS' ? '#333333' : '#6b7280',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  ROAS cible
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                {objectiveForm.type === 'CPA' ? 'CPA cible (€)' : 'ROAS cible (%)'}
              </label>
              <input
                type="number"
                value={objectiveModalValues.currentValue}
                onChange={(e) => {
                  const field = objectiveForm.type === 'CPA' ? 'cpaValue' : 'roasValue';
                  setObjectiveForm(prev => ({ ...prev, [field]: e.target.value }));
                }}
                placeholder={objectiveForm.type === 'CPA' ? 'ex: 35.50' : 'ex: 150'}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                min="0"
                step={objectiveForm.type === 'CPA' ? '0.01' : '1'}
              />
              <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#9ca3af' }}>
                {objectiveForm.type === 'CPA'
                  ? 'Le coût par acquisition cible en euros'
                  : 'Le retour sur dépenses publicitaires cible en pourcentage (ex: 150 = 150%)'
                }
              </p>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditingObjective(null)}
                style={{
                  padding: '10px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Annuler
              </button>
              <button
                onClick={saveCustomObjective}
                disabled={objectiveModalValues.isDisabled}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: objectiveModalValues.isDisabled ? '#9ca3af' : '#333333',
                  color: 'white',
                  fontWeight: '500',
                  cursor: objectiveModalValues.isDisabled ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {savingObjective ? (
                  <>Sauvegarde...</>
                ) : (
                  <>
                    <Check style={{ width: '16px', height: '16px' }} />
                    Enregistrer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceBidAdjustmentsTool;
